// Apify integration for scraping camp images
const APIFY_API_TOKEN = import.meta.env.VITE_APIFY_API_TOKEN;
const APIFY_USER_ID = import.meta.env.VITE_APIFY_USER_ID || 'apify'; // Default to campwhiz user ID if not set
const APIFY_BASE_URL = 'https://api.apify.com/v2';

// Debug: Log the first few characters of the token to verify which one is being used
console.log('[APIFY DEBUG] Token starts with:', APIFY_API_TOKEN ? APIFY_API_TOKEN.substring(0, 10) : 'undefined');
console.log('[APIFY DEBUG] User ID:', APIFY_USER_ID);

interface ApifyScrapingResult {
  url: string;
  title?: string;
  images?: string[];
  logo?: string;
}

interface ApifyRunResponse {
  data: {
    id: string;
    status: string;
    defaultDatasetId: string;
  };
}

interface ApifyDatasetResponse {
  data: ApifyScrapingResult[];
}

// Add a module-level lock for Apify concurrency
let apifyLock: Promise<void> | null = null;
let isApifyRunning = false;
let currentCancellationToken: { cancelled: boolean } | null = null;

// Function to reset Apify state (called when skipping camps)
export function resetApifyState() {
  console.log('🔄 Resetting Apify state...');
  isApifyRunning = false;
  if (currentCancellationToken) {
    currentCancellationToken.cancelled = true;
    currentCancellationToken = null;
  }
  apifyLock = null;
  
  // Force cancel any ongoing operations by creating a new cancellation token
  currentCancellationToken = { cancelled: true };
  
  // Clear any pending promises
  if (apifyLock) {
    apifyLock = Promise.reject(new Error('Apify operation cancelled'));
  }
}

function withApifyLock<T>(fn: () => Promise<T>): Promise<T> {
  if (isApifyRunning) {
    console.log('⏸️ Apify already running, waiting for completion...');
    return new Promise((resolve, reject) => {
      const checkAndRun = () => {
        if (!isApifyRunning) {
          isApifyRunning = true;
          currentCancellationToken = { cancelled: false };
          fn().then(resolve).catch(reject).finally(() => {
            isApifyRunning = false;
            currentCancellationToken = null;
          });
        } else {
          setTimeout(checkAndRun, 100);
        }
      };
      checkAndRun();
    });
  }
  
  isApifyRunning = true;
  currentCancellationToken = { cancelled: false };
  return fn().finally(() => {
    isApifyRunning = false;
    currentCancellationToken = null;
  });
}

// Test function to verify Apify integration
export async function testApifyConnection(): Promise<{ success: boolean; message: string }> {
  if (!APIFY_API_TOKEN) {
    return { 
      success: false, 
      message: 'Apify API token not configured. Please add VITE_APIFY_API_TOKEN to your .env file.' 
    };
  }

  try {
    // Test with a simple website
    const testUrl = 'https://example.com';
    console.log('Testing Apify connection with:', testUrl);
    
    const result = await scrapeCampImages(testUrl);
    
    return { 
      success: true, 
      message: `Apify connection successful! Found ${result.photos.length} images and ${result.logo ? '1' : '0'} logos.` 
    };
  } catch (error) {
    console.error('Apify test failed:', error);
    return { 
      success: false, 
      message: `Apify test failed: ${error instanceof Error ? error.message : 'Unknown error'}` 
    };
  }
}

// Test function specifically for Florida Fencing Academy logo detection
export async function testFloridaFencingAcademy(): Promise<{ success: boolean; message: string; logo?: string; images: string[] }> {
  if (!APIFY_API_TOKEN) {
    return { 
      success: false, 
      message: 'Apify API token not configured. Please add VITE_APIFY_API_TOKEN to your .env file.',
      images: []
    };
  }

  try {
    const testUrl = 'https://floridafencingacademy.wildapricot.org/';
    console.log('Testing Florida Fencing Academy logo detection with:', testUrl);
    
    const result = await scrapeCampImages(testUrl);
    
    console.log('Florida Fencing Academy scraping result:', {
      logo: result.logo,
      photosCount: result.photos.length,
      photos: result.photos.slice(0, 5) // Log first 5 photos
    });
    
    return { 
      success: true, 
      message: `Florida Fencing Academy test completed! Found ${result.photos.length} images and ${result.logo ? '1' : '0'} logos.`,
      logo: result.logo,
      images: result.photos
    };
  } catch (error) {
    console.error('Florida Fencing Academy test failed:', error);
    return { 
      success: false, 
      message: `Florida Fencing Academy test failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
      images: []
    };
  }
}

export async function scrapeCampImages(campWebsite: string): Promise<{
  logo?: string;
  photos: string[];
  cancel?: () => void;
}> {
  return withApifyLock(async () => {
    if (!APIFY_API_TOKEN) {
      console.warn('Apify API token not configured');
      return { photos: [] };
    }

    const isCancelled = false;
    const checkCancelled = () => {
      if (isCancelled || (currentCancellationToken && currentCancellationToken.cancelled)) {
        console.log('🚫 Apify scraping cancelled');
        throw new Error('Scraping cancelled');
      }
    };

    try {
      console.log('Starting Apify scraping for:', campWebsite);
      console.log('Using API token:', APIFY_API_TOKEN ? 'Present (hidden for security)' : 'Missing');

      // Check for cancellation before starting
      checkCancelled();

      // Try Cheerio Scraper first (faster, simpler sites)
      console.log('Starting Cheerio scraper...');
      try {
        console.log('About to call scrapeWithCheerio...');
        const cheerioResult = await scrapeWithCheerio(campWebsite, checkCancelled);
        
        // Check for cancellation after Cheerio
        checkCancelled();
        
        console.log('scrapeWithCheerio returned successfully');
        console.log('Cheerio scraper result in main function:', {
          photosCount: cheerioResult.photos.length,
          hasLogo: !!cheerioResult.logo,
          photos: cheerioResult.photos.slice(0, 3), // Log first 3 photos
          logo: cheerioResult.logo
        });
        
        // Check if Cheerio found any images
        if (cheerioResult.photos.length > 0 || cheerioResult.logo) {
          console.log('Cheerio scraper found images, returning results');
          return cheerioResult;
        } else {
          console.log('Cheerio scraper succeeded but found no images, trying Web Scraper...');
        }
      } catch (cheerioError) {
        // Check if this was a cancellation error
        if (cheerioError instanceof Error && cheerioError.message === 'Scraping cancelled') {
          console.log('🚫 Cheerio scraper cancelled');
          throw cheerioError;
        }
        
        console.error('Cheerio scraper failed with error:', cheerioError);
        if (cheerioError instanceof Error) {
          console.error('Error stack:', cheerioError.stack);
        }
        console.warn('Trying Web Scraper as fallback...');
      }

      // Check for cancellation before Web Scraper
      checkCancelled();

      // If Cheerio found nothing or failed, try Web Scraper (slower but handles JS)
      console.log('Trying Web Scraper as fallback...');
      try {
        const webResult = await scrapeWithWebScraper(campWebsite, checkCancelled);
        
        // Check for cancellation after Web Scraper
        checkCancelled();
        
        console.log('Web Scraper result:', {
          photosCount: webResult.photos.length,
          hasLogo: !!webResult.logo,
          photos: webResult.photos.slice(0, 3)
        });
        
        // If Web Scraper also found nothing, return empty result
        if (webResult.photos.length === 0 && !webResult.logo) {
          console.log('Both Cheerio and Web Scraper found no images');
          return { photos: [], cancel: () => {} };
        }
        
        return webResult;
      } catch (webError) {
        // Check if this was a cancellation error
        if (webError instanceof Error && webError.message === 'Scraping cancelled') {
          console.log('🚫 Web Scraper cancelled');
          throw webError;
        }
        
        console.warn('Web Scraper also failed:', webError);
        return { photos: [], cancel: () => {} };
      }

    } catch (error) {
      // Check if this was a cancellation error
      if (error instanceof Error && error.message === 'Scraping cancelled') {
        console.log('🚫 Apify scraping cancelled');
        throw error;
      }
      
      console.error('Apify scraping error:', error);
      
      // As a last resort, try a simple fallback approach
      console.log('Trying simple fallback image extraction...');
      try {
        checkCancelled(); // Check before fallback
        const fallbackResult = await simpleImageFallback(campWebsite);
        checkCancelled(); // Check after fallback
        if (fallbackResult.photos.length > 0 || fallbackResult.logo) {
          console.log('Fallback found some images:', fallbackResult);
          return fallbackResult;
        }
      } catch (fallbackError) {
        // Check if this was a cancellation error
        if (fallbackError instanceof Error && fallbackError.message === 'Scraping cancelled') {
          console.log('🚫 Fallback cancelled');
          throw fallbackError;
        }
        
        console.warn('Fallback also failed:', fallbackError);
      }
      
      return { photos: [], cancel: () => {} };
    }
  });
}

async function scrapeWithCheerio(campWebsite: string, checkCancelled: () => void): Promise<{
  logo?: string;
  photos: string[];
  cancel?: () => void;
}> {
  try {
    console.log('scrapeWithCheerio called with:', campWebsite);
    let isCancelled = false;
    
    // Check for cancellation before starting
    checkCancelled();
    
    console.log('Making Cheerio API call to:', `${APIFY_BASE_URL}/acts/apify~cheerio-scraper/runs`);
    const runResponse = await fetch(`${APIFY_BASE_URL}/acts/apify~cheerio-scraper/runs`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${APIFY_API_TOKEN}`
      },
      body: JSON.stringify({
        startUrls: [{ url: campWebsite }],
        pageFunction: `
          async function pageFunction(context) {
            const { $, request, log } = context;
            
            try {
              // Log the current URL and page info for debugging
              log.info('Scraping URL: ' + request.url);
              log.info('Loaded URL: ' + (request.loadedUrl || 'not available'));
              log.info('Page title: ' + $('title').text().trim());
              log.info('Total img tags found: ' + $('img').length);
              
              // Extract all images from the page with logo detection
              const images = [];
              const logos = [];
              const metaImages = [];

              // Extract og:image and twitter:image meta tags
              const ogImage = $('meta[property="og:image"]').attr('content');
              if (ogImage && typeof ogImage === 'string' && ogImage.startsWith('http')) {
                metaImages.push(ogImage);
                log.info('Found og:image meta tag: ' + ogImage);
              }
              const twitterImage = $('meta[name="twitter:image"]').attr('content');
              if (twitterImage && typeof twitterImage === 'string' && twitterImage.startsWith('http')) {
                metaImages.push(twitterImage);
                log.info('Found twitter:image meta tag: ' + twitterImage);
              }

              $('img').each((i, el) => {
                const $img = $(el);
                let src = $img.attr('src');
                const alt = $img.attr('alt') || '';
                const className = $img.attr('class') || '';
                const id = $img.attr('id') || '';
                
                // Convert relative URLs to absolute URLs
                if (src && !src.startsWith('http') && !src.startsWith('data:')) {
                  try {
                    // Use the loaded URL (after redirect) as the base URL
                    const baseUrl = new URL(request.loadedUrl || request.url);
                    src = new URL(src, baseUrl).href;
                    log.info('Converted relative URL to absolute: ' + src);
                  } catch (e) {
                    log.info('Failed to convert relative URL: ' + src);
                    src = null;
                  }
                }
                
                // Fix URLs that still point to the old domain (feyc.org -> floridaelks.org)
                if (src && src.includes('feyc.org')) {
                  src = src.replace('http://www.feyc.org', 'https://floridaelks.org');
                  src = src.replace('https://www.feyc.org', 'https://floridaelks.org');
                  log.info('Fixed old domain URL: ' + src);
                }
                
                log.info('Image ' + i + ': src=' + src + ', alt=' + alt + ', class=' + className);
                
                if (src && src.startsWith('http') && !src.includes('data:')) {
                  // Check if this looks like a logo - more precise detection
                  const isLogo = 
                    alt.toLowerCase().includes('logo') ||
                    // More specific logo class detection (avoid generic responsive classes)
                    /logo|brand|site-logo/i.test(className) ||
                    id.toLowerCase().includes('logo') ||
                    src.toLowerCase().includes('logo') ||
                    // Check for common logo filename patterns
                    /logo[a-z]*\.(png|jpg|jpeg|gif|svg)$/i.test(src) ||
                    // Check for logo in specific parent elements (not too broadly)
                    $img.closest('header .logo, nav .logo, .header .logo, .nav .logo, .logo').length > 0;
                  
                  if (isLogo) {
                    logos.push(src);
                    log.info('Found logo: ' + src + ' (alt: ' + alt + ', class: ' + className + ', id: ' + id + ')');
                  } else {
                    images.push(src);
                    log.info('Found image: ' + src + ' (alt: ' + alt + ', class: ' + className + ', id: ' + id + ')');
                  }
                } else if (src) {
                  log.info('Skipped image (not http or data): ' + src);
                }
              });

              // Deduplicate and prioritize meta images
              const allImages = Array.from(new Set([...metaImages, ...images]));
              log.info('Final image list (meta + img tags, deduped): ' + JSON.stringify(allImages));
              log.info('Found ' + allImages.length + ' total images and ' + logos.length + ' logos on page');
              
              return {
                url: request.url,
                title: $('title').text().trim(),
                images: allImages.slice(0, 10), // Limit to 10 images
                logo: logos[0] || null // Take the first logo found
              };
            } catch (error) {
              log.error('Error in pageFunction:', error.message);
              return {
                url: request.url,
                title: 'Error',
                images: [],
                logo: null,
                error: error.message
              };
            }
          }
        `,
        proxy: {
          useApifyProxy: true,
          apifyProxyGroups: ['RESIDENTIAL']
        }
      })
    });

    if (!runResponse.ok) {
      const errorText = await runResponse.text();
      console.error('Apify API error response:', {
        status: runResponse.status,
        statusText: runResponse.statusText,
        errorText: errorText,
        headers: Object.fromEntries(runResponse.headers.entries())
      });
      throw new Error(`Apify API error: ${runResponse.status} - ${errorText}`);
    }

    const responseText = await runResponse.text();
    console.log('Raw Apify response:', responseText);
    
    let runData: ApifyRunResponse;
    try {
      runData = JSON.parse(responseText);
    } catch (parseError) {
      console.error('Failed to parse Apify response:', parseError);
      throw new Error(`Invalid JSON response from Apify: ${responseText}`);
    }
    console.log('Apify run started:', runData);
    
    // Validate that we got a proper run ID
    if (!runData.data?.id) {
      console.error('No run ID received from Apify:', runData);
      throw new Error('Failed to start Apify run - no run ID received');
    }
    
         // Wait for the run to complete
     let attempts = 0;
     const maxAttempts = 60; // Increased to 60 seconds timeout for slower websites
    
    while (attempts < maxAttempts) {
      checkCancelled(); // Check if scraping should be cancelled
      await new Promise(resolve => setTimeout(resolve, 1000)); // Wait 1 second
      
      try {
        const statusResponse = await fetch(`${APIFY_BASE_URL}/acts/apify~cheerio-scraper/runs/${runData.data.id}`, {
          headers: {
            'Authorization': `Bearer ${APIFY_API_TOKEN}`
          }
        });
        
        if (statusResponse.ok) {
          const statusData = await statusResponse.json();
          console.log(`Apify run status (attempt ${attempts + 1}):`, statusData);
          
          // Check the actual status field - it might be nested differently
          const runStatus = statusData.data?.status || statusData.status;
          console.log(`Apify run status (attempt ${attempts + 1}):`, runStatus);
          
          if (runStatus === 'SUCCEEDED') {
            break;
          } else if (runStatus === 'FAILED') {
            console.error('Apify run FAILED - this means the scraper encountered an error');
            console.error('This could be due to:');
            console.error('- Website blocking the scraper');
            console.error('- JavaScript errors on the page');
            console.error('- Network issues');
            console.error('- Invalid pageFunction');
            return { photos: [], cancel: () => {} };
          } else if (runStatus === 'ABORTED') {
            console.warn('Apify run aborted, continuing without scraping');
            return { photos: [], cancel: () => {} };
          } else if (runStatus === 'RUNNING' || runStatus === 'READY') {
            // Continue waiting
            console.log(`Apify run is ${runStatus}, waiting...`);
          } else if (runStatus === 'TIMED-OUT') {
            console.warn('Apify run timed out, continuing without scraping');
            return { photos: [], cancel: () => {} };
          } else {
            console.log(`Apify run status: ${runStatus}, waiting...`);
          }
        } else {
          const errorText = await statusResponse.text();
          console.warn(`Apify status check failed (${statusResponse.status}): ${errorText}`);
          return { photos: [], cancel: () => {} };
        }
      } catch (statusError) {
        console.warn('Apify status check error, continuing without scraping:', statusError);
        return { photos: [], cancel: () => {} };
      }
      
      attempts++;
    }
    
    if (attempts >= maxAttempts) {
      console.warn('Apify scraping timed out, continuing without scraping');
      return { photos: [], cancel: () => {} };
    }

    console.log('Apify run completed, fetching results...');

    // Get the results
    const datasetResponse = await fetch(`${APIFY_BASE_URL}/datasets/${runData.data.defaultDatasetId}/items`, {
      headers: {
        'Authorization': `Bearer ${APIFY_API_TOKEN}`
      }
    });

    if (!datasetResponse.ok) {
      const errorText = await datasetResponse.text();
      console.error('Dataset fetch error:', errorText);
      throw new Error(`Failed to fetch dataset: ${datasetResponse.status} - ${errorText}`);
    }

    const datasetData: ApifyDatasetResponse = await datasetResponse.json();
    console.log('Apify results received:', JSON.stringify(datasetData, null, 2));
    
    // The response is an array directly, not wrapped in a data property
    if (!datasetData || !Array.isArray(datasetData) || datasetData.length === 0) {
      console.log('No data returned from Apify or invalid data structure');
      return { photos: [] };
    }

    const result = datasetData[0];
    if (!result) {
      console.log('No result object in dataset');
      return { photos: [] };
    }
    console.log('Apify scraping result:', {
      url: result.url,
      title: result.title,
      imagesFound: result.images?.length || 0,
      logoFound: !!result.logo,
      images: result.images?.slice(0, 3), // Log first 3 images
      logo: result.logo,
      fullResult: result // Log the full result to see the structure
    });
    
    // Check if we have images or logo
    const images = result.images || [];
    const hasImages = Array.isArray(images) && images.length > 0;
    const hasLogo = result.logo && result.logo !== null;
    console.log('Cheerio result check:', { hasImages, hasLogo, imagesCount: images.length, logoValue: result.logo });
    
    const finalResult = {
      logo: result.logo || undefined,
      photos: images,
      cancel: () => {
        isCancelled = true;
        console.log('Apify scraping cancelled');
      }
    };
    
    console.log('Cheerio final result:', {
      photosCount: finalResult.photos.length,
      hasLogo: !!finalResult.logo,
      photos: finalResult.photos.slice(0, 3)
    });
    
    return finalResult;
  } catch (error: any) {
    if (error && error.message && error.message.includes('402')) {
      console.error('Apify quota exceeded (402 Payment Required). Skipping image scraping for this camp.');
      return { photos: [], cancel: () => {} };
    }
    throw error;
  }
}

async function scrapeWithWebScraper(campWebsite: string, checkCancelled: () => void): Promise<{
  logo?: string;
  photos: string[];
  cancel?: () => void;
}> {
  try {
    console.log('Starting Web Scraper for:', campWebsite);
    
    const runResponse = await fetch(`${APIFY_BASE_URL}/acts/apify~web-scraper/runs`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${APIFY_API_TOKEN}`
      },
      body: JSON.stringify({
        startUrls: [{ url: campWebsite }],
        pageFunction: `
          async function pageFunction(context) {
            const { page, request, log } = context;
            
            try {
              // Check if page is available
              if (!page) {
                log.error('Page object is not available');
                return {
                  url: request.url,
                  title: 'Error',
                  images: [],
                  logo: null,
                  error: 'Page object is not available'
                };
              }
              
              // Wait for page to load with error handling
              try {
                // Use a more compatible approach for waiting
                await new Promise(resolve => setTimeout(resolve, 3000));
              } catch (timeoutError) {
                log.warn('Timeout error, continuing anyway:', timeoutError.message);
              }
              
              // Extract images with logo detection
              let images = [];
              let logos = [];
              try {
                const imageData = await page.evaluate(() => {
                  const imgElements = document.querySelectorAll('img');
                  const allImages = [];
                  const allLogos = [];
                  
                  imgElements.forEach(img => {
                    const src = img.src;
                    const alt = img.alt || '';
                    const className = img.className || '';
                    const id = img.id || '';
                    
                    if (src && src.startsWith('http') && !src.includes('data:')) {
                      // Check if this looks like a logo - more precise detection
                      const isLogo = 
                        alt.toLowerCase().includes('logo') ||
                        // More specific logo class detection (avoid generic responsive classes)
                        /logo|brand|site-logo/i.test(className) ||
                        id.toLowerCase().includes('logo') ||
                        src.toLowerCase().includes('logo') ||
                        // Check for common logo filename patterns
                        /logo[a-z]*\.(png|jpg|jpeg|gif|svg)$/i.test(src) ||
                        // Check for logo in specific parent elements (not too broadly)
                        img.closest('header .logo, nav .logo, .header .logo, .nav .logo, .logo');
                      
                      if (isLogo) {
                        allLogos.push(src);
                      } else {
                        allImages.push(src);
                      }
                    }
                  });
                  
                  return {
                    images: allImages,
                    logos: allLogos
                  };
                });
                
                images = imageData.images;
                logos = imageData.logos;
              } catch (evaluateError) {
                log.error('Error evaluating page:', evaluateError.message);
              }
              
              let title = 'Unknown';
              try {
                title = await page.title();
              } catch (titleError) {
                log.warn('Error getting title:', titleError.message);
              }
              
              return {
                url: request.url,
                title: title,
                images: images.slice(0, 10),
                logo: logos[0] || null // Take the first logo found
              };
            } catch (error) {
              log.error('Error in pageFunction:', error.message);
              return {
                url: request.url,
                title: 'Error',
                images: [],
                logo: null,
                error: error.message
              };
            }
          }
        `,
        proxy: {
          useApifyProxy: true,
          apifyProxyGroups: ['RESIDENTIAL']
        }
      })
    });

    if (!runResponse.ok) {
      const errorText = await runResponse.text();
      console.error('Web Scraper API error:', errorText);
      return { photos: [], cancel: () => {} };
    }

    const runData = await runResponse.json();
    console.log('Web Scraper run started:', runData);
    
    if (!runData.data?.id) {
      console.error('No run ID received from Web Scraper');
      return { photos: [], cancel: () => {} };
    }
    
         // Wait for the run to complete (shorter timeout for Web Scraper)
     let attempts = 0;
     const maxAttempts = 6; // Reduced timeout for Web Scraper to prevent hanging
    
    while (attempts < maxAttempts) {
      checkCancelled();
      await new Promise(resolve => setTimeout(resolve, 2000)); // Wait 2 seconds
      
      try {
        const statusResponse = await fetch(`${APIFY_BASE_URL}/acts/apify~web-scraper/runs/${runData.data.id}`, {
          headers: {
            'Authorization': `Bearer ${APIFY_API_TOKEN}`
          }
        });
        
        if (statusResponse.ok) {
          const statusData = await statusResponse.json();
          const runStatus = statusData.data?.status || statusData.status;
          console.log(`Web Scraper status (attempt ${attempts + 1}):`, runStatus);
          
          if (runStatus === 'SUCCEEDED') {
            break;
          } else if (runStatus === 'FAILED' || runStatus === 'ABORTED') {
            console.warn('Web Scraper run failed');
            return { photos: [], cancel: () => {} };
          }
        }
      } catch (statusError) {
        console.warn('Web Scraper status check error:', statusError);
        return { photos: [], cancel: () => {} };
      }
      
      attempts++;
    }
    
    if (attempts >= maxAttempts) {
      console.warn('Web Scraper timed out after', maxAttempts * 2, 'seconds');
      return { photos: [], cancel: () => {} };
    }

    // Get the results
    const datasetResponse = await fetch(`${APIFY_BASE_URL}/datasets/${runData.data.defaultDatasetId}/items`, {
      headers: {
        'Authorization': `Bearer ${APIFY_API_TOKEN}`
      }
    });

    if (!datasetResponse.ok) {
      console.error('Web Scraper dataset fetch failed');
      return { photos: [], cancel: () => {} };
    }

    const datasetData = await datasetResponse.json();
    console.log('Web Scraper results:', JSON.stringify(datasetData, null, 2));
    
    if (!datasetData.data || datasetData.data.length === 0) {
      console.log('Web Scraper returned no data');
      return { photos: [] };
    }

    const result = datasetData.data[0];
    console.log('Web Scraper result:', {
      url: result.url,
      title: result.title,
      imagesFound: result.images?.length || 0,
      logoFound: !!result.logo,
      hasError: !!result['#error'],
      errorDetails: result['#error'] ? result['#debug'] : null
    });
    
    // Check if there was an error in the scraping
    if (result['#error'] || result.error) {
      console.warn('Web Scraper encountered an error:', result.error || result['#error']);
      return { photos: [] };
    }
    
    return {
      logo: result.logo || undefined,
      photos: result.images || [],
      cancel: () => {
        console.log('Web Scraper cancelled');
      }
    };
  } catch (error: any) {
    if (error && error.message && error.message.includes('402')) {
      console.error('Apify quota exceeded (402 Payment Required). Skipping image scraping for this camp.');
      return { photos: [], cancel: () => {} };
    }
    throw error;
  }
}

// Alternative: Use Apify's Web Scraper for more complex sites
export async function scrapeCampImagesAdvanced(campWebsite: string): Promise<{
  logo?: string;
  photos: string[];
}> {
  if (!APIFY_API_TOKEN) {
    console.warn('Apify API token not configured');
    return { photos: [] };
  }

  try {
    // Use the Web Scraper actor for more complex sites
    const runResponse = await fetch(`${APIFY_BASE_URL}/acts/apify~web-scraper/runs`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${APIFY_API_TOKEN}`
      },
      body: JSON.stringify({
        startUrls: [{ url: campWebsite }],
        pageFunction: `
          async function pageFunction(context) {
            const { page, request, log } = context;
            
            // Wait for page to load
            await new Promise(resolve => setTimeout(resolve, 2000));
            
            // Extract images
            const images = await page.evaluate(() => {
              const imgElements = document.querySelectorAll('img');
              const images = [];
              const logos = [];
              
              imgElements.forEach(img => {
                const src = img.src;
                const alt = img.alt || '';
                const className = img.className || '';
                
                if (src && !src.includes('data:') && !src.includes('placeholder')) {
                  // Check if this looks like a logo
                  const isLogo = alt.toLowerCase().includes('logo') || 
                                className.toLowerCase().includes('logo') ||
                                img.closest('header, nav, .header, .nav') ||
                                img.closest('[class*="logo"]');
                  
                  if (isLogo) {
                    logos.push(src);
                  } else {
                    images.push(src);
                  }
                }
              });
              
              return { images, logos };
            });
            
            return {
              url: request.url,
              title: await page.title(),
              images: images.images.slice(0, 10),
              logo: images.logos[0] || null
            };
          }
        `,
        proxy: {
          useApifyProxy: true,
          apifyProxyGroups: ['RESIDENTIAL']
        }
      })
    });

    // ... rest of the implementation is similar to the Cheerio version
    // (I'll keep this shorter for brevity, but you can implement the same waiting logic)

  } catch (error) {
    console.error('Apify advanced scraping error:', error);
    return { photos: [] };
  }
}

// Simple fallback function for when Apify scrapers fail
async function simpleImageFallback(campWebsite: string): Promise<{
  logo?: string;
  photos: string[];
}> {
  try {
    console.log('Simple fallback: trying direct fetch to extract basic image info');
    
    // Try to fetch the page directly to get basic image URLs
    const response = await fetch(campWebsite, {
      headers: {
        'User-Agent': 'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/138.0.0.0 Safari/537.36'
      }
    });
    
    if (!response.ok) {
      console.log('Simple fallback: fetch failed with status', response.status);
      return { photos: [] };
    }
    
    const html = await response.text();
    
    // Simple regex to extract image URLs
    const imgRegex = /<img[^>]+src=["']([^"']+)["'][^>]*>/gi;
    const images: string[] = [];
    let match;
    
    while ((match = imgRegex.exec(html)) !== null) {
      const src = match[1];
      if (src && src.startsWith('http') && !src.includes('data:')) {
        images.push(src);
      }
    }
    
    console.log('Simple fallback: found', images.length, 'images');
    return {
      photos: images.slice(0, 5), // Limit to 5 images
      logo: undefined
    };
    
  } catch (error) {
    console.warn('Simple fallback failed:', error);
    return { photos: [] };
  }
} 

/**
 * Scrape visible text content from a camp website using local Playwright (if localhost), then Apify as fallback.
 * Returns the best visible text content found.
 */
export async function scrapePageTextWithFallback(campWebsite: string): Promise<{ text: string; html?: string; method: 'playwright-local' | 'cheerio' | 'puppeteer' | 'none'; }> {
  console.log('🔍 [scrapePageTextWithFallback] Starting text extraction for:', campWebsite);
  
  // Check if we're on localhost - use local scraper first
  // Detect both client-side (window) and server-side (Node.js) environments
  const isClientSide = typeof window !== 'undefined';
  const isServerSide = typeof process !== 'undefined' && process.versions?.node;
  const isLocalhost = isClientSide && (
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1' ||
    window.location.hostname === ''
  ) || isServerSide; // On server, always use local scraper
  
  if (isLocalhost) {
    try {
      console.log('🏠 [scrapePageTextWithFallback] Using local Playwright scraper (localhost detected)');
      
      // Build the API URL - use absolute URL for server-side, relative for client-side
      const apiUrl = isServerSide 
        ? `http://localhost:${process.env.PORT || 5173}/api/scrape-website`
        : '/api/scrape-website';
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: campWebsite })
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.text && data.text.length > 100) {
          console.log('✅ [scrapePageTextWithFallback] Local scraper succeeded');
          return { text: data.text, html: data.html, method: 'playwright-local' };
        }
      }
      console.warn('⚠️ [scrapePageTextWithFallback] Local scraper failed, falling back to Apify');
    } catch (error) {
      console.warn('⚠️ [scrapePageTextWithFallback] Local scraper error, falling back to Apify:', error);
    }
  }
  
  // Fallback to Apify if not localhost or local scraper failed
  if (!APIFY_API_TOKEN) {
    console.warn('❌ Apify API token not configured');
    return { text: '', method: 'none' };
  }

  function isBoilerplate(text: string): boolean {
    // Consider text boilerplate if it's very short or contains mostly script/style/SSR junk
    // But be more lenient for Wix sites that might have some JavaScript artifacts
    if (!text || text.length < 100) return true;
    
    // Check if it's mostly JavaScript/SSR artifacts
    const jsArtifacts = /window\.|ReactDOM|__APOLLO_STATE__|<script|<style|function\s*\(|var\s+|document\.|console\./g;
    const matches = text.match(jsArtifacts);
    const artifactRatio = matches ? matches.length / text.length : 0;
    
    // If more than 10% of the text is JavaScript artifacts, consider it boilerplate
    return artifactRatio > 0.1;
  }

  // Try Cheerio first
  logScrapePhase('cheerio', campWebsite);
  const cheerioResult = await scrapePageTextWithCheerio(campWebsite);
  console.log('[scrapePageTextWithFallback] Cheerio text (first 500 chars):', cheerioResult.text.slice(0, 500));
  if (!isBoilerplate(cheerioResult.text)) {
    console.log('[scrapePageTextWithFallback] Cheerio returned usable text.');
    return { text: cheerioResult.text, html: cheerioResult.html, method: 'cheerio' };
  } else {
    console.warn('[scrapePageTextWithFallback] Cheerio returned boilerplate or empty text.');
  }

  // Try Puppeteer fallback (Apify)
  logScrapePhase('puppeteer', campWebsite);
  try {
    const puppeteerText = await scrapePageTextWithPuppeteer(campWebsite);
    console.log('[scrapePageTextWithFallback] Puppeteer text (first 500 chars):', puppeteerText.slice(0, 500));
    if (!isBoilerplate(puppeteerText)) {
      console.log('[scrapePageTextWithFallback] Puppeteer returned usable text.');
      return { text: puppeteerText, method: 'puppeteer' };
    } else {
      console.warn('[scrapePageTextWithFallback] Puppeteer also returned boilerplate or empty text.');
    }
  } catch (apifyError) {
    console.warn('[scrapePageTextWithFallback] Apify Puppeteer failed:', apifyError);
    // If Apify fails (e.g., quota exceeded), try local scraper again if we haven't already
    if (!isLocalhost) {
      try {
        console.log('🔄 [scrapePageTextWithFallback] Apify failed, trying local scraper as last resort');
        
        // Build the API URL - use absolute URL for server-side, relative for client-side
        const apiUrl = isServerSide 
          ? `http://localhost:${process.env.PORT || 5173}/api/scrape-website`
          : '/api/scrape-website';
        
        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ url: campWebsite })
        });
        
        if (response.ok) {
          const data = await response.json();
          if (data.text && data.text.length > 100) {
            console.log('✅ [scrapePageTextWithFallback] Local scraper succeeded as fallback');
            return { text: data.text, html: data.html, method: 'playwright-local' };
          }
        }
      } catch (localError) {
        console.warn('[scrapePageTextWithFallback] Local scraper fallback also failed:', localError);
      }
    }
  }

  // All methods failed
  console.error('[scrapePageTextWithFallback] All scraping methods failed for', campWebsite);
  console.error('[scrapePageTextWithFallback] This may indicate:');
  console.error('[scrapePageTextWithFallback] 1. Website is blocking scraping');
  console.error('[scrapePageTextWithFallback] 2. Website requires JavaScript to render content');
  console.error('[scrapePageTextWithFallback] 3. Website is down or unreachable');
  console.error('[scrapePageTextWithFallback] 4. Apify service is experiencing issues (quota exceeded?)');
  return { text: '', method: 'none' };
}

/**
 * Scrape visible text content using Cheerio (apify/cheerio-scraper)
 */
async function scrapePageTextWithCheerio(campWebsite: string): Promise<{ text: string; html?: string }> {
  logScrapePhase('cheerio', campWebsite);
  console.log('[scrapePageTextWithCheerio] Starting text extraction for:', campWebsite);
  
  // Add timeout to prevent hanging
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout
  
  try {
    const runResponse = await fetch(`${APIFY_BASE_URL}/acts/apify~cheerio-scraper/runs`, {
      signal: controller.signal,
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${APIFY_API_TOKEN}`
    },
    body: JSON.stringify({
      startUrls: [{ url: campWebsite }],
      pageFunction: `async function pageFunction(context) {
        const { $, request, log } = context;
        
        // Test if pageFunction is being called
        log.info('=== PAGE FUNCTION STARTED ===');
        log.info('Context keys: ' + Object.keys(context).join(', '));
        log.info('Request URL: ' + request.url);
        log.info('Request method: ' + request.method);
        log.info('Request headers: ' + JSON.stringify(request.headers));
        
        try {
            log.info('Starting text extraction for: ' + request.url);
            log.info('Request body exists: ' + (request.body ? 'YES' : 'NO'));
            log.info('Request body length: ' + (request.body ? request.body.length : 0));
            log.info('Request status code: ' + request.statusCode);
            
          // Remove script and style tags to get cleaner text
          $('script, style, noscript').remove();
          
            // Discover camp-related subpages from the main page
            const discovered = new Set();
            
            // 1. All <a href> links - be more aggressive about finding camp-related content
            const allLinks = $('a[href]');
            log.info('Total links found on page: ' + allLinks.length);
            
            allLinks.each((i, el) => {
              let href = $(el).attr('href');
              const linkText = $(el).text().toLowerCase().trim();
              
              // Log first few links for debugging
              if (i < 10) {
                log.info('Link ' + i + ': href="' + href + '", text="' + linkText + '"');
              }
              
              // Skip empty links or common non-content links
              if (!href || 
                  ['#', 'javascript:', 'mailto:', 'tel:'].some(prefix => href.toLowerCase().startsWith(prefix))) {
                return;
              }
              
              // Check if link contains camp-related terms OR location names
              const campTerms = ['camp', 'summer', 'program', 'activity', 'enrollment', 'registration', 'schedule', 'dates', 'pricing', 'tuition'];
              const locationTerms = ['sarasota', 'lakewood', 'ranch', 'bee ridge', 'proctor'];
              const relevantTerms = [...campTerms, ...locationTerms];
              
              const isRelevant = relevantTerms.some(term => 
                href.toLowerCase().includes(term) || linkText.includes(term)
              );
              
              if (isRelevant) {
                // Normalize relative URLs
                if (!/^https?:\\/\\//.test(href)) {
                  try {
                    href = new URL(href, request.url).href;
                  } catch {
                    return;
                  }
                }
                discovered.add(href);
                log.info('Found camp-related link: ' + href + ' (text: "' + linkText + '")');
              }
            });
            
            // 2. Only add a few essential fallback URLs if no links were found
            if (discovered.size === 0) {
              log.info('No links found, adding minimal fallback URLs');
              const essentialFallbacks = ['summer-camp', 'camps', 'programs'];
              
              essentialFallbacks.forEach((path) => {
                try {
                  discovered.add(new URL('/' + path, request.url).href);
                } catch {
                  // Skip if URL construction fails
                }
              });
            }
            
            const subpages = Array.from(discovered);
            log.info('Discovered camp subpages: ' + JSON.stringify(subpages));
            log.info('Total subpages found: ' + subpages.length);
            
            // Log each subpage for debugging
            subpages.forEach((url, index) => {
              log.info('Subpage ' + (index + 1) + ': ' + url);
            });
            
            // Simple but effective text extraction
            let allText = '';
            
            try {
              // Extract meta description
              const metaDescription = $('meta[name="description"]').attr('content') || '';
              if (metaDescription) {
                allText += 'Meta Description: ' + metaDescription + '\\n\\n';
                log.info('Found meta description: ' + metaDescription.substring(0, 100));
              }
              
              // Extract title
              const title = $('title').text() || '';
              if (title) {
                allText += 'Page Title: ' + title + '\\n\\n';
                log.info('Found title: ' + title);
              }
              
              // Extract structured data (JSON-LD) - simplified
              const jsonLdScripts = $('script[type="application/ld+json"]');
              log.info('Found ' + jsonLdScripts.length + ' JSON-LD scripts');
              jsonLdScripts.each((i, el) => {
                try {
                  const scriptContent = $(el).html();
                  if (scriptContent) {
                    allText += 'Structured Data: ' + scriptContent + '\\n\\n';
                    log.info('Added JSON-LD script ' + (i + 1) + ' (length: ' + scriptContent.length + ')');
                  }
                } catch (e) {
                  log.info('Error processing JSON-LD: ' + e.message);
                }
              });
              
              // Extract visible text from body
              let bodyText = $('body').text() || '';
              bodyText = bodyText.replace(/\\s+/g, ' ').replace(/[\\u00A0\\u200B\\u200C\\u200D\\uFEFF]/g, ' ').trim();
              
              log.info('Body text length: ' + bodyText.length);
              if (bodyText.length > 0) {
                log.info('Body text preview: ' + bodyText.substring(0, 200));
              }
              
              // Add body text
              allText += bodyText;
              
            } catch (extractError) {
              log.error('Error in text extraction: ' + extractError.message);
              // Fallback: just get body text
              allText = $('body').text() || '';
              allText = allText.replace(/\\s+/g, ' ').replace(/[\\u00A0\\u200B\\u200C\\u200D\\uFEFF]/g, ' ').trim();
            }
            
            log.info('Main page text length: ' + allText.length);
            log.info('Main page text preview: ' + allText.substring(0, 500));
            
            // Extract and aggregate text from each subpage
            for (const subpageUrl of subpages) {
              try {
                if (subpageUrl === request.url) continue; // Skip main page
                
                // Use context.httpRequest to fetch subpages (server-side, no CORS)
                const subpageResponse = await context.httpRequest(subpageUrl);
                if (subpageResponse.statusCode === 200) {
                  const subpageHtml = subpageResponse.body;
                  const $sub = context.cheerio.load(subpageHtml);
                  $sub('script, style, noscript').remove();
                  
                  // Simple subpage text extraction
                  const subBodyText = $sub('body').text() || '';
                  const cleanedSubText = subBodyText.replace(/\\s+/g, ' ').replace(/[\\u00A0\\u200B\\u200C\\u200D\\uFEFF]/g, ' ').trim();
                  
                  let subText = cleanedSubText;
                  
                  log.info('Subpage ' + subpageUrl + ' text length: ' + subText.length);
                  if (subText.length > 100) {
                    allText += '\\n\\n--- SUBPAGE: ' + subpageUrl + ' ---\\n' + subText;
                  }
                } else {
                  log.info('Failed to fetch subpage: ' + subpageUrl + ' (status ' + subpageResponse.statusCode + ')');
                }
              } catch (err) {
                log.info('Error fetching subpage ' + subpageUrl + ': ' + err.message);
              }
            }
            
            log.info('Final text length: ' + allText.length);
            log.info('Text preview (first 500 chars): ' + allText.substring(0, 500));
            
            // Also return HTML for social media extraction
            const htmlContent = $.html();
            log.info('HTML content length: ' + htmlContent.length);
            
          return { url: request.url, allText, htmlContent };
        } catch (error) {
          log.error('Error extracting text:', error.message);
          log.error('Error stack:', error.stack);
          return { url: request.url, allText: '', error: error.message };
        }
      }`,
      proxy: {
        useApifyProxy: true,
        apifyProxyGroups: ['RESIDENTIAL']
      }
    })
  });
    
    console.log('[scrapePageTextWithCheerio] Apify run response status:', runResponse.status);
    
    if (!runResponse.ok) {
      const errorText = await runResponse.text();
      console.error('[scrapePageTextWithCheerio] Apify API error:', errorText);
      throw new Error('Cheerio text scrape failed: ' + errorText);
    }
    
  const runData = await runResponse.json();
    console.log('[scrapePageTextWithCheerio] Apify run started:', runData);
    
    if (!runData.data?.id) {
      console.error('[scrapePageTextWithCheerio] No run ID received:', runData);
      throw new Error('No run ID from Cheerio');
    }
    
  // Wait for run to complete
  let attempts = 0;
  const maxAttempts = 30;
  while (attempts < maxAttempts) {
    await new Promise(res => setTimeout(res, 2000));
    const statusResponse = await fetch(`${APIFY_BASE_URL}/acts/apify~cheerio-scraper/runs/${runData.data.id}`, {
      headers: { 'Authorization': `Bearer ${APIFY_API_TOKEN}` }
    });
      
      if (!statusResponse.ok) {
        console.error('[scrapePageTextWithCheerio] Status check failed:', statusResponse.status);
        break;
      }
      
    const statusData = await statusResponse.json();
    const runStatus = statusData.data?.status || statusData.status;
      console.log(`[scrapePageTextWithCheerio] Run status (attempt ${attempts + 1}):`, runStatus);
      
    if (runStatus === 'SUCCEEDED') break;
      if (runStatus === 'FAILED' || runStatus === 'ABORTED') {
        console.error('[scrapePageTextWithCheerio] Run failed:', statusData);
        throw new Error('Cheerio run failed');
      }
    attempts++;
  }
    
    if (attempts >= maxAttempts) {
      console.error('[scrapePageTextWithCheerio] Run timed out');
      throw new Error('Cheerio run timed out');
    }
    
  // Get the results
  const datasetResponse = await fetch(`${APIFY_BASE_URL}/datasets/${runData.data.defaultDatasetId}/items`, {
    headers: { 'Authorization': `Bearer ${APIFY_API_TOKEN}` }
  });
    
    if (!datasetResponse.ok) {
      const errorText = await datasetResponse.text();
      console.error('[scrapePageTextWithCheerio] Dataset fetch failed:', errorText);
      throw new Error('Failed to fetch dataset: ' + errorText);
    }
    
  const datasetData = await datasetResponse.json();
    console.log('[scrapePageTextWithCheerio] Dataset response:', JSON.stringify(datasetData, null, 2));
    
    if (!Array.isArray(datasetData) || !datasetData[0]) {
      console.error('[scrapePageTextWithCheerio] No data in dataset:', datasetData);
      return { text: '', html: undefined };
    }
    
    const result = datasetData[0];
    console.log('[scrapePageTextWithCheerio] Result:', result);
    
    const extractedText = result.allText || '';
    console.log('[scrapePageTextWithCheerio] Extracted text length:', extractedText.length);
    console.log('[scrapePageTextWithCheerio] Text preview:', extractedText.substring(0, 500));
    
    // Add client-side logging to see what was actually extracted
    if (result.error) {
      console.log('[scrapePageTextWithCheerio] ❌ ERROR in pageFunction:', result.error);
    }
    
    if (extractedText.length > 0) {
      console.log('[scrapePageTextWithCheerio] ✅ SUCCESS: Found content!');
      console.log('[scrapePageTextWithCheerio] Content includes meta description:', extractedText.includes('Meta Description:'));
      console.log('[scrapePageTextWithCheerio] Content includes title:', extractedText.includes('Page Title:'));
      console.log('[scrapePageTextWithCheerio] Content includes structured data:', extractedText.includes('Structured Data:'));
      console.log('[scrapePageTextWithCheerio] Content includes subpages:', extractedText.includes('SUBPAGE:'));
    } else {
      console.log('[scrapePageTextWithCheerio] ❌ FAILURE: No content extracted');
      console.log('[scrapePageTextWithCheerio] Raw result:', result);
    }
    
    return { 
      text: extractedText, 
      html: result.htmlContent 
    };
    
  } catch (error) {
    console.error('[scrapePageTextWithCheerio] Error:', error);
    clearTimeout(timeoutId);
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * Scrape visible text content using Puppeteer (apify/web-scraper)
 */
async function scrapePageTextWithPuppeteer(campWebsite: string): Promise<string> {
  logScrapePhase('puppeteer', campWebsite);
  
  // Add timeout to prevent hanging
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout
  
  try {
    const runResponse = await fetch(`${APIFY_BASE_URL}/acts/apify~web-scraper/runs`, {
      signal: controller.signal,
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${APIFY_API_TOKEN}`
    },
    body: JSON.stringify({
      startUrls: [{ url: campWebsite }],
      proxyConfiguration: {
        "useApifyProxy": true,
        "apifyProxyGroups": ["RESIDENTIAL"]
      },
      pageFunction: `async function pageFunction(context) {
        const { page, request, log } = context;
        try {
          // Set a modern user agent
          await page.setUserAgent('Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/120.0.0.0 Safari/537.36');
          
          // Navigate to page
          await page.goto(request.url, { waitUntil: 'networkidle2', timeout: 30000 });
          
          // Check for Cloudflare challenge
          const cloudflareCheck = await page.evaluate(() => {
            const bodyText = document.body.textContent || '';
            const title = document.title || '';
            return {
              hasCloudflare: bodyText.includes('Checking your browser') || 
                            bodyText.includes('Just a moment') ||
                            bodyText.includes('DDoS protection by Cloudflare') ||
                            title.includes('Just a moment') ||
                            document.querySelector('#cf-wrapper') !== null ||
                            document.querySelector('.cf-browser-verification') !== null,
              hasContent: bodyText.length > 500 && !bodyText.includes('Please wait') && !bodyText.includes('Checking your browser')
            };
          });
          
          if (cloudflareCheck.hasCloudflare && !cloudflareCheck.hasContent) {
            log.warn('Cloudflare challenge detected, waiting for it to resolve...');
            // Wait for Cloudflare challenge to resolve (up to 30 seconds)
            await page.waitForFunction(() => {
              const bodyText = document.body.textContent || '';
              return bodyText.length > 500 && 
                     !bodyText.includes('Checking your browser') && 
                     !bodyText.includes('Just a moment') &&
                     !bodyText.includes('Please wait');
            }, { timeout: 30000 }).catch(() => {
              log.warn('Cloudflare challenge did not resolve in time');
            });
          }
          
          // Wait for actual content to appear (not just time-based)
          // Try waiting for common content indicators
          const contentSelectors = [
            'main', 'article', '.content', '#content', '.main-content',
            'h1', 'h2', '.camp', '.program', '.schedule', '.pricing'
          ];
          
          let contentFound = false;
          for (const selector of contentSelectors) {
            try {
              await page.waitForSelector(selector, { timeout: 5000 });
              const element = await page.$(selector);
              if (element) {
                const text = await page.evaluate(el => el.textContent || '', element);
                if (text && text.trim().length > 200) {
                  contentFound = true;
                  log.info('Found content using selector: ' + selector);
                  break;
                }
              }
            } catch (e) {
              // Selector not found, try next one
            }
          }
          
          // If no content found yet, wait a bit more for JS to render
          if (!contentFound) {
            log.info('Waiting for JavaScript to render content...');
            await page.waitForFunction(() => {
              const bodyText = document.body.textContent || '';
              return bodyText.length > 500;
            }, { timeout: 15000 }).catch(() => {
              log.warn('Content did not appear after waiting');
            });
          }
          
          // Remove script and style tags
          await page.evaluate(() => {
            document.querySelectorAll('script, style, noscript').forEach(el => el.remove());
          });
          
          // Site builder selectors
          const SITE_BUILDERS_SELECTORS = [
            // Wix
            '[data-hook="site-content"]',
            '[data-testid="richTextElement"]',
            '[data-testid="textElement"]',
            '[data-testid="containerBg"]',
            '[data-testid="page-bg"]',
            // Squarespace
            '.sqs-block-content',
            '.sqs-block-html',
            '.sqs-block-text',
            '.sqs-block-image',
            '.sqs-block-button',
            '.sqs-block-code',
            '.sqs-block-form',
            '.sqs-block-gallery',
            '.sqs-block-map',
            '.sqs-block-social',
            '.sqs-block-video',
            '.sqs-block-audio',
            '.sqs-block-embed',
            '.sqs-block-quote',
            '.sqs-block-spacer',
            '.sqs-block-summary',
            '.sqs-block-calendar',
            '.sqs-block-accordion',
            '.sqs-block-markdown',
            // Weebly
            '.weebly-section',
            '.weebly-content',
            '.weebly-text',
            '.weebly-image',
            '.weebly-gallery',
            '.weebly-map',
            '.weebly-social',
            '.weebly-video',
            '.weebly-audio',
            '.weebly-embed',
            '.weebly-quote',
            '.weebly-spacer',
            '.weebly-summary',
            '.weebly-calendar',
            '.weebly-accordion',
            '.weebly-markdown',
            // WordPress
            '.wp-block',
            '.wp-block-paragraph',
            '.wp-block-heading',
            '.wp-block-image',
            '.wp-block-gallery',
            '.wp-block-list',
            '.wp-block-quote',
            '.wp-block-cover',
            '.wp-block-video',
            '.wp-block-audio',
            '.wp-block-table',
            '.wp-block-columns',
            '.wp-block-group',
            '.wp-block-button',
            '.wp-block-code',
            '.wp-block-embed',
            '.wp-block-html',
            '.wp-block-preformatted',
            '.wp-block-pullquote',
            '.wp-block-separator',
            '.wp-block-spacer',
            '.wp-block-verse',
            '.wp-block-widget',
            '.wp-block-navigation',
            '.wp-block-site-title',
            '.wp-block-site-tagline',
            '.wp-block-site-logo',
            '.wp-block-post-title',
            '.wp-block-post-content',
            '.wp-block-post-excerpt',
            '.wp-block-post-date',
            '.wp-block-post-author',
            '.wp-block-post-terms',
            '.wp-block-post-featured-image',
            '.wp-block-post-navigation-link',
            '.wp-block-query',
            '.wp-block-query-title',
            '.wp-block-query-loop',
            '.wp-block-query-pagination',
            // Generic fallbacks
            '.content', '.main', '.article', 'article', 'section', '#content', '#main', 'main', 'body',
            'p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'div', 'span'
          ];
          
          let mainText = '';
          let selectorLog = [];
          
          for (const selector of SITE_BUILDERS_SELECTORS) {
            try {
              const elements = await page.$$(selector);
              if (elements.length > 0) {
                selectorLog.push(selector + ': ' + elements.length + ' elements');
                for (const element of elements) {
                  const text = await page.evaluate(el => el.textContent || el.innerText || '', element);
                  if (text && text.trim().length > 50) {
                    mainText += text.trim() + ' ';
                    log.info('Extracted text from ' + selector + ' (first 200 chars): ' + text.substring(0, 200) + '...');
                  }
                }
                if (mainText.trim().length > 200) break;
              }
            } catch (e) {
              log.info('Error with selector ' + selector + ': ' + e.message);
            }
          }
          
          // Fallback to body text if no main content found
          if (!mainText || mainText.trim().length < 200) {
            mainText = await page.evaluate(() => document.body.textContent || document.body.innerText || '');
            selectorLog.push('body (fallback): ' + mainText.length + ' chars');
            log.info('Fallback to body text (first 500 chars): ' + mainText.substring(0, 500) + '...');
          }
          
          // Post-processing
          let cleanedText = mainText
            .replace(/\\s+/g, ' ') // Remove extra whitespace
            .replace(/[^\\w\\s.,!?;:()\\-\\'"]/g, '') // Remove special characters but keep basic punctuation
            .replace(/\\b(Powered by|All rights reserved|Privacy Policy|Terms of Service|Cookie Policy|©|Copyright)\\b/gi, '') // Remove boilerplate
            .replace(/\\b(Home|About|Contact|Services|Products|Blog|News|Events|Gallery|FAQ|Support|Login|Sign up|Subscribe|Newsletter)\\b/gi, '') // Remove navigation
            .replace(/\\b(Follow us|Like us|Share|Tweet|Instagram|Facebook|Twitter|LinkedIn|YouTube|Pinterest|TikTok)\\b/gi, '') // Remove social media
            .replace(/\\b(Phone|Email|Address|Location|Hours|Monday|Tuesday|Wednesday|Thursday|Friday|Saturday|Sunday)\\b/gi, '') // Remove contact info
            .replace(/\\b(Camp|Summer|Day|Overnight|Residential|Program|Activity|Schedule|Registration|Enrollment|Tuition|Fees|Discount|Scholarship)\\b/gi, '') // Remove camp-specific terms
            .replace(/\\s+/g, ' ') // Clean up whitespace again
            .trim();
          
          log.info('Selector hits: ' + JSON.stringify(selectorLog));
          log.info('Original text length: ' + mainText.length);
          log.info('Cleaned text length: ' + cleanedText.length);
          
          return {
            url: request.url,
            mainText: cleanedText || mainText || '',
            title: await page.title()
          };
        } catch (error) {
          log.error('Error in pageFunction:', error);
          return {
            url: request.url,
            mainText: '',
            title: '',
            error: error.message
          };
        }
      }`,
      waitFor: 15000
    })
  });
  
  if (!runResponse.ok) {
    clearTimeout(timeoutId);
    throw new Error('Puppeteer text scrape failed');
  }
  
  const runData = await runResponse.json();
  if (!runData.data?.id) {
    clearTimeout(timeoutId);
    throw new Error('No run ID from Puppeteer');
  }
  
      // Wait for run to complete
    let attempts = 0;
    const maxAttempts = 10;
    while (attempts < maxAttempts) {
      await new Promise(res => setTimeout(res, 1000));
      console.log(`[scrapePageTextWithPuppeteer] Checking status attempt ${attempts + 1}/${maxAttempts} for ${campWebsite}`);
      
      const statusResponse = await fetch(`${APIFY_BASE_URL}/acts/apify~web-scraper/runs/${runData.data.id}`, {
        headers: { 'Authorization': `Bearer ${APIFY_API_TOKEN}` }
      });
      const statusData = await statusResponse.json();
      const runStatus = statusData.data?.status || statusData.status;
      
      console.log(`[scrapePageTextWithPuppeteer] Run status: ${runStatus} for ${campWebsite}`);
      
      if (runStatus === 'SUCCEEDED') break;
      if (runStatus === 'FAILED' || runStatus === 'ABORTED') {
        clearTimeout(timeoutId);
        throw new Error('Puppeteer run failed');
      }
      attempts++;
    }
  
  // Get the results
  const datasetResponse = await fetch(`${APIFY_BASE_URL}/datasets/${runData.data.defaultDatasetId}/items`, {
    headers: { 'Authorization': `Bearer ${APIFY_API_TOKEN}` }
  });
  const datasetData = await datasetResponse.json();
  
  clearTimeout(timeoutId);
  
  if (!Array.isArray(datasetData) || !datasetData[0]) return '';
  return datasetData[0].mainText || '';
  
  } catch (error) {
    console.error('[scrapePageTextWithPuppeteer] Error:', error);
    clearTimeout(timeoutId);
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
} 

// Helper to discover camp-related subpages from homepage
function discoverCampSubpages($: any, baseUrl: string): string[] {
  const discovered = new Set<string>();

  // Only extract real links found in the page HTML
  $('a[href]').each((i: number, el: any) => {
    let href = $(el).attr('href');
    let linkText = $(el).text().toLowerCase().trim();
    
    // Check for camp-related keywords in href or link text
    const campKeywords = [
      'camp', 'summer', 'program', 'session', 'registration', 
      'dates', 'rates', 'schedule', 'activities', 'enrollment',
      'tuition', 'fee', 'cost', 'age', 'grade', 'week', 'parent',
      'contact', 'about', 'info', 'us', 'staff', 'facilities',
      'location', 'directions', 'policies', 'philosophy', 'offerings',
      'community', 'listing', 'courses', 'workshops',
      'youth', 'children', 'kids', 'teen', 'adult', 'family'
    ];
    
    // More inclusive discovery - look for any content pages, not just camp-specific ones
    const hasCampKeyword = campKeywords.some(keyword =>
      (href && href.toLowerCase().includes(keyword)) || 
      linkText.includes(keyword)
    );
    
    // Also include pages that look like content pages (not just camp-specific)
    // Prioritize summer camp, program pages, and contact pages
    const looksLikeContentPage = href && (
      href.includes('/summer-camp') ||
      href.includes('summer-camp-program') ||
      href.includes('/summer-camp-program') ||
      href.includes('/page/') || 
      href.includes('/program') ||
      href.includes('/summer') ||
      href.includes('/session') ||
      href.includes('/course') ||
      href.includes('/workshop') ||
      href.includes('/offering') ||
      href.includes('/offerings') ||
      href.includes('/community') ||
      href.includes('/about') ||
      href.includes('/info') ||
      href.includes('/dates') ||
      href.includes('/schedule') ||
      href.includes('/registration') ||
      href.includes('/enrollment') ||
      href.includes('/tuition') ||
      href.includes('/fee') ||
      href.includes('/cost') ||
      href.includes('/rates') ||
      href.includes('/activities') ||
      href.includes('/staff') ||
      href.includes('/facilities') ||
      href.includes('/location') ||
      href.includes('/contact') ||
      href.includes('contact-us') ||
      href.includes('contactus') ||
      href.includes('get-in-touch') ||
      href.includes('reach-us') ||
      href.includes('/policies') ||
      href.includes('/philosophy') ||
      href.includes('/directions') ||
      href.includes('/parent') ||
      href.includes('/family') ||
      href.includes('/youth') ||
      href.includes('/children') ||
      href.includes('/kids') ||
      href.includes('/teen') ||
      href.includes('/adult') ||
      href.includes('/age') ||
      href.includes('/grade') ||
      href.includes('/week') ||
      href.includes('/ecms') ||
      href.includes('/institute') ||
      href.includes('/institutes')
    );
    
    // Exclude image files and other non-page URLs
    const isImageFile = /\.(jpg|jpeg|png|gif|svg|webp|ico|bmp|tiff)$/i.test(href);
    const isAssetFile = /wp-content\/uploads|assets|images|css|js/i.test(href);
    const isExternalLink = href && !href.includes(new URL(baseUrl).hostname);
    
    if (href && (hasCampKeyword || looksLikeContentPage) && !isImageFile && !isAssetFile && !isExternalLink) {
      // Normalize relative URLs
      if (!/^https?:\/\//.test(href)) {
        try {
          href = new URL(href, baseUrl).href;
        } catch {
          // If URL construction fails, skip
          return;
        }
      }
      discovered.add(href);
      console.log(`🔍 Found camp-related link: ${href} (text: "${linkText}")`);
    }
  });

  console.log(`🔍 Discovered ${discovered.size} camp-related subpages from real links`);
  
  // Sort discovered pages - prioritize contact pages, then summer camp and program pages
  const sortedPages = Array.from(discovered).sort((a, b) => {
    const aLower = a.toLowerCase();
    const bLower = b.toLowerCase();
    
    // Check for contact page indicators
    const aIsContact = aLower.includes('/contact') || 
                       aLower.includes('contact-us') || 
                       aLower.includes('contactus') ||
                       aLower.includes('contact/') ||
                       aLower.includes('get-in-touch') ||
                       aLower.includes('reach-us');
    const bIsContact = bLower.includes('/contact') || 
                       bLower.includes('contact-us') || 
                       bLower.includes('contactus') ||
                       bLower.includes('contact/') ||
                       bLower.includes('get-in-touch') ||
                       bLower.includes('reach-us');
    
    // Contact pages first (highest priority for email extraction)
    if (aIsContact && !bIsContact) return -1;
    if (!aIsContact && bIsContact) return 1;
    
    // Then summer camp pages
    const aIsSummerCamp = aLower.includes('summer-camp') || aLower.includes('summer-camp-program');
    const bIsSummerCamp = bLower.includes('summer-camp') || bLower.includes('summer-camp-program');
    if (aIsSummerCamp && !bIsSummerCamp) return -1;
    if (!aIsSummerCamp && bIsSummerCamp) return 1;
    
    // Then program pages
    const aIsProgram = aLower.includes('/program');
    const bIsProgram = bLower.includes('/program');
    if (aIsProgram && !bIsProgram) return -1;
    if (!aIsProgram && bIsProgram) return 1;
    
    return 0;
  });
  
  return sortedPages;
}

// Add a helper to log the phase and URL
function logScrapePhase(phase: string, url: string) {
  console.log(`[SCRAPER DEBUG] Phase: ${phase} | URL: ${url}`);
}

// Example usage before each scrape:
// logScrapePhase('main-page', url);
// logScrapePhase('subpage', url);
// logScrapePhase('images', url);

// On timeout or error:
// console.error(`[SCRAPER TIMEOUT/ERROR] Phase: ${phase} | URL: ${url} | Error:`, error);

/**
 * Fast scraping function that reduces execution time by using shorter timeouts
 * and parallel processing where possible
 */
export async function scrapeCampImagesFast(campWebsite: string): Promise<{
  logo?: string;
  photos: string[];
  cancel?: () => void;
}> {
  return withApifyLock(async () => {
    if (!APIFY_API_TOKEN) {
      console.warn('Apify API token not configured');
      return { photos: [] };
    }

    const isCancelled = false;
    const checkCancelled = () => {
      if (isCancelled || (currentCancellationToken && currentCancellationToken.cancelled)) {
        console.log('🚫 Apify scraping cancelled');
        throw new Error('Scraping cancelled');
      }
    };

    try {
      console.log('Starting fast Apify scraping for:', campWebsite);

      // Check for cancellation before starting
      checkCancelled();

      // Try Cheerio Scraper with reduced timeout
      console.log('Starting fast Cheerio scraper...');
      try {
        const cheerioResult = await scrapeWithCheerioFast(campWebsite, checkCancelled);
        
        // Check for cancellation after Cheerio
        checkCancelled();
        
        console.log('Fast Cheerio scraper result:', {
          photosCount: cheerioResult.photos.length,
          hasLogo: !!cheerioResult.logo
        });
        
        // If Cheerio found images, return immediately
        if (cheerioResult.photos.length > 0 || cheerioResult.logo) {
          console.log('Fast Cheerio scraper found images, returning results');
          return cheerioResult;
        }
      } catch (cheerioError) {
        if (cheerioError instanceof Error && cheerioError.message === 'Scraping cancelled') {
          throw cheerioError;
        }
        console.warn('Fast Cheerio scraper failed, trying Web Scraper...');
      }

      // Check for cancellation before Web Scraper
      checkCancelled();

      // Try Web Scraper with reduced timeout
      console.log('Trying fast Web Scraper...');
      try {
        const webResult = await scrapeWithWebScraperFast(campWebsite, checkCancelled);
        
        checkCancelled();
        
        console.log('Fast Web Scraper result:', {
          photosCount: webResult.photos.length,
          hasLogo: !!webResult.logo
        });
        
        return webResult;
      } catch (webError) {
        if (webError instanceof Error && webError.message === 'Scraping cancelled') {
          throw webError;
        }
        
        console.warn('Fast Web Scraper also failed');
        return { photos: [], cancel: () => {} };
      }
    } catch (error) {
      console.error('Fast scraping failed:', error);
      return { photos: [], cancel: () => {} };
    }
  });
}

/**
 * Fast Cheerio scraping with reduced timeouts
 */
async function scrapeWithCheerioFast(campWebsite: string, checkCancelled: () => void): Promise<{
  logo?: string;
  photos: string[];
  cancel?: () => void;
}> {
  try {
    console.log('scrapeWithCheerioFast called with:', campWebsite);
    let isCancelled = false;
    
    checkCancelled();
    
    console.log('Making fast Cheerio API call...');
    const runResponse = await fetch(`${APIFY_BASE_URL}/acts/apify~cheerio-scraper/runs`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${APIFY_API_TOKEN}`
      },
      body: JSON.stringify({
        startUrls: [{ url: campWebsite }],
        pageFunction: `
          async function pageFunction(context) {
            const { $, request, log } = context;
            
            try {
              log.info('Fast scraping URL: ' + request.url);
              
              // Extract all images from the page with logo detection
              const images = [];
              const logos = [];
              const metaImages = [];

              // Extract og:image and twitter:image meta tags
              const ogImage = $('meta[property="og:image"]').attr('content');
              if (ogImage && typeof ogImage === 'string' && ogImage.startsWith('http')) {
                metaImages.push(ogImage);
              }
              const twitterImage = $('meta[name="twitter:image"]').attr('content');
              if (twitterImage && typeof twitterImage === 'string' && twitterImage.startsWith('http')) {
                metaImages.push(twitterImage);
              }

              $('img').each((i, el) => {
                const $img = $(el);
                let src = $img.attr('src');
                const alt = $img.attr('alt') || '';
                const className = $img.attr('class') || '';
                const id = $img.attr('id') || '';
                
                // Convert relative URLs to absolute URLs
                if (src && !src.startsWith('http') && !src.startsWith('data:')) {
                  try {
                    const baseUrl = new URL(request.loadedUrl || request.url);
                    src = new URL(src, baseUrl).href;
                  } catch (e) {
                    src = null;
                  }
                }
                
                if (src && src.startsWith('http') && !src.includes('data:')) {
                  // Check if this looks like a logo
                  const isLogo = 
                    alt.toLowerCase().includes('logo') ||
                    /logo|brand|site-logo/i.test(className) ||
                    id.toLowerCase().includes('logo') ||
                    src.toLowerCase().includes('logo') ||
                    $img.closest('header .logo, nav .logo, .header .logo, .nav .logo, .logo').length > 0;
                  
                  if (isLogo) {
                    logos.push(src);
                  } else {
                    images.push(src);
                  }
                }
              });

              // Deduplicate and prioritize meta images
              const allImages = Array.from(new Set([...metaImages, ...images]));
              
              return {
                url: request.url,
                title: $('title').text().trim(),
                images: allImages.slice(0, 10), // Limit to 10 images
                logo: logos[0] || null
              };
            } catch (error) {
              log.error('Error in fast pageFunction:', error.message);
              return {
                url: request.url,
                title: 'Error',
                images: [],
                logo: null,
                error: error.message
              };
            }
          }
        `,
        proxy: {
          useApifyProxy: true,
          apifyProxyGroups: ['RESIDENTIAL']
        }
      })
    });

    if (!runResponse.ok) {
      const errorText = await runResponse.text();
      console.error('Fast Cheerio API error:', errorText);
      throw new Error('Fast Cheerio scrape failed: ' + errorText);
    }

    const runData = await runResponse.json();
    console.log('Fast Cheerio run started:', runData);

    if (!runData.data?.id) {
      throw new Error('No run ID from fast Cheerio');
    }

    // Wait for run to complete with timeout
    let attempts = 0;
    const maxAttempts = 60; // Increased back to 60 for better reliability on slower websites
    while (attempts < maxAttempts) {
      await new Promise(res => setTimeout(res, 1000));
      const statusResponse = await fetch(`${APIFY_BASE_URL}/acts/apify~cheerio-scraper/runs/${runData.data.id}`, {
        headers: { 'Authorization': `Bearer ${APIFY_API_TOKEN}` }
      });
      
      if (!statusResponse.ok) {
        console.error('Fast Cheerio status check failed:', statusResponse.status);
        break;
      }
      
      const statusData = await statusResponse.json();
      const runStatus = statusData.data?.status || statusData.status;
      
      if (runStatus === 'SUCCEEDED') break;
      if (runStatus === 'FAILED' || runStatus === 'ABORTED') {
        throw new Error('Fast Cheerio run failed');
      }
      attempts++;
    }
    
    if (attempts >= maxAttempts) {
      throw new Error('Fast Cheerio run timed out');
    }

    // Get the results
    const datasetResponse = await fetch(`${APIFY_BASE_URL}/datasets/${runData.data.defaultDatasetId}/items`, {
      headers: { 'Authorization': `Bearer ${APIFY_API_TOKEN}` }
    });

    if (!datasetResponse.ok) {
      throw new Error('Failed to fetch fast Cheerio dataset');
    }

    const datasetData = await datasetResponse.json();
    
    if (!Array.isArray(datasetData) || !datasetData[0]) {
      return { photos: [] };
    }

    const result = datasetData[0];
    const images = result.images || [];
    const hasImages = Array.isArray(images) && images.length > 0;
    const hasLogo = result.logo && result.logo !== null;
    
    return {
      logo: result.logo || undefined,
      photos: images,
      cancel: () => {
        isCancelled = true;
        console.log('Fast Cheerio scraping cancelled');
      }
    };
  } catch (error: any) {
    if (error && error.message && error.message.includes('402')) {
      console.error('Apify quota exceeded. Skipping fast image scraping.');
      return { photos: [], cancel: () => {} };
    }
    throw error;
  }
}

/**
 * Fast Web Scraper with reduced timeouts
 */
async function scrapeWithWebScraperFast(campWebsite: string, checkCancelled: () => void): Promise<{
  logo?: string;
  photos: string[];
  cancel?: () => void;
}> {
  try {
    console.log('Starting fast Web Scraper for:', campWebsite);
    
    const runResponse = await fetch(`${APIFY_BASE_URL}/acts/apify~web-scraper/runs`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${APIFY_API_TOKEN}`
      },
      body: JSON.stringify({
        startUrls: [{ url: campWebsite }],
        pageFunction: `
          async function pageFunction(context) {
            const { page, request, log } = context;
            
            try {
              if (!page) {
                return {
                  url: request.url,
                  title: 'Error',
                  images: [],
                  logo: null,
                  error: 'Page object is not available'
                };
              }
              
              // Reduced wait time from 15s to 5s
              await new Promise(resolve => setTimeout(resolve, 5000));
              
              // Extract images with logo detection
              let images = [];
              let logos = [];
              try {
                const imageData = await page.evaluate(() => {
                  const imgElements = document.querySelectorAll('img');
                  const allImages = [];
                  const allLogos = [];
                  
                  imgElements.forEach(img => {
                    const src = img.src;
                    const alt = img.alt || '';
                    const className = img.className || '';
                    const id = img.id || '';
                    
                    if (src && src.startsWith('http') && !src.includes('data:')) {
                      const isLogo = 
                        alt.toLowerCase().includes('logo') ||
                        /logo|brand|site-logo/i.test(className) ||
                        id.toLowerCase().includes('logo') ||
                        src.toLowerCase().includes('logo') ||
                        img.closest('header .logo, nav .logo, .header .logo, .nav .logo, .logo');
                      
                      if (isLogo) {
                        allLogos.push(src);
                      } else {
                        allImages.push(src);
                      }
                    }
                  });
                  
                  return {
                    images: allImages,
                    logos: allLogos
                  };
                });
                
                images = imageData.images;
                logos = imageData.logos;
              } catch (evaluateError) {
                log.error('Error evaluating page:', evaluateError.message);
              }
              
              let title = 'Unknown';
              try {
                title = await page.title();
              } catch (titleError) {
                log.warn('Error getting title:', titleError.message);
              }
              
              return {
                url: request.url,
                title: title,
                images: images.slice(0, 10),
                logo: logos[0] || null
              };
            } catch (error) {
              log.error('Error in fast pageFunction:', error.message);
              return {
                url: request.url,
                title: 'Error',
                images: [],
                logo: null,
                error: error.message
              };
            }
          }
        `,
        proxy: {
          useApifyProxy: true,
          apifyProxyGroups: ['RESIDENTIAL']
        }
      })
    });

    if (!runResponse.ok) {
      throw new Error('Fast Web Scraper API call failed');
    }

    const runData = await runResponse.json();
    if (!runData.data?.id) {
      throw new Error('No run ID from fast Web Scraper');
    }

    // Wait for run to complete with reduced timeout
    let attempts = 0;
    const maxAttempts = 8; // Reduced from 10
    while (attempts < maxAttempts) {
      await new Promise(res => setTimeout(res, 1000));
      const statusResponse = await fetch(`${APIFY_BASE_URL}/acts/apify~web-scraper/runs/${runData.data.id}`, {
        headers: { 'Authorization': `Bearer ${APIFY_API_TOKEN}` }
      });
      const statusData = await statusResponse.json();
      const runStatus = statusData.data?.status || statusData.status;
      if (runStatus === 'SUCCEEDED') break;
      if (runStatus === 'FAILED' || runStatus === 'ABORTED') {
        throw new Error('Fast Web Scraper run failed');
      }
      attempts++;
    }

    // Get the results
    const datasetResponse = await fetch(`${APIFY_BASE_URL}/datasets/${runData.data.defaultDatasetId}/items`, {
      headers: { 'Authorization': `Bearer ${APIFY_API_TOKEN}` }
    });

    if (!datasetResponse.ok) {
      return { photos: [], cancel: () => {} };
    }

    const datasetData = await datasetResponse.json();
    
    if (!Array.isArray(datasetData) || !datasetData[0]) {
      return { photos: [] };
    }

    const result = datasetData[0];
    
    if (result['#error'] || result.error) {
      console.warn('Fast Web Scraper encountered an error:', result.error || result['#error']);
      return { photos: [] };
    }
    
    return {
      logo: result.logo || undefined,
      photos: result.images || [],
      cancel: () => {
        console.log('Fast Web Scraper cancelled');
      }
    };
  } catch (error: any) {
    if (error && error.message && error.message.includes('402')) {
      console.error('Apify quota exceeded. Skipping fast image scraping.');
      return { photos: [], cancel: () => {} };
    }
    throw error;
  }
}

/**
 * Fast text scraping with reduced timeouts and simplified logic
 */
export async function scrapePageTextFast(campWebsite: string): Promise<{ text: string; html?: string; method: 'cheerio' | 'puppeteer' | 'none'; }> {
  console.log('🔍 [scrapePageTextFast] Starting fast text extraction for:', campWebsite);
  
  if (!APIFY_API_TOKEN) {
    console.warn('❌ Apify API token not configured');
    return { text: '', method: 'none' };
  }

  function isBoilerplate(text: string): boolean {
    if (!text || text.length < 100) return true;
    
    const jsArtifacts = /window\.|ReactDOM|__APOLLO_STATE__|<script|<style|function\s*\(|var\s+|document\.|console\./g;
    const matches = text.match(jsArtifacts);
    const artifactRatio = matches ? matches.length / text.length : 0;
    
    return artifactRatio > 0.1;
  }

  // Try Cheerio first with fast timeout
  try {
    const cheerioResult = await scrapePageTextWithCheerioFast(campWebsite);
    if (!isBoilerplate(cheerioResult.text)) {
      console.log('[scrapePageTextFast] Cheerio returned usable text.');
      return { text: cheerioResult.text, html: cheerioResult.html, method: 'cheerio' };
    }
  } catch (error) {
    console.warn('[scrapePageTextFast] Cheerio failed:', error);
  }

  // Try Puppeteer fallback with fast timeout
  try {
    const puppeteerText = await scrapePageTextWithPuppeteerFast(campWebsite);
    if (!isBoilerplate(puppeteerText)) {
      console.log('[scrapePageTextFast] Puppeteer returned usable text.');
      return { text: puppeteerText, method: 'puppeteer' };
    }
  } catch (error) {
    console.warn('[scrapePageTextFast] Puppeteer failed:', error);
  }

  console.error('[scrapePageTextFast] Both methods failed to extract usable text');
  return { text: '', method: 'none' };
}

/**
 * Comprehensive text scraping that includes sub-pages like contact, about, etc.
 * This is more thorough than scrapePageTextFast but takes longer
 */
export async function scrapePageTextComprehensive(campWebsite: string): Promise<{ 
  text: string; 
  html?: string; 
  method: 'playwright-local' | 'cheerio' | 'puppeteer' | 'none';
  subPagesContent?: string[];
  emails?: string[];
}> {
  console.log('🔍 [scrapePageTextComprehensive] Starting comprehensive text extraction for:', campWebsite);
  
  // Check if we're on localhost - use local scraper first
  // Detect both client-side (window) and server-side (Node.js) environments
  const isClientSide = typeof window !== 'undefined';
  const isServerSide = typeof process !== 'undefined' && process.versions?.node;
  const isLocalhost = isClientSide && (
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1' ||
    window.location.hostname === ''
  ) || isServerSide; // On server, always use local scraper
  
  if (isLocalhost) {
    try {
      console.log('🏠 [scrapePageTextComprehensive] Using local Playwright scraper (localhost detected)');
      
      // Build the API URL - use absolute URL for server-side, relative for client-side
      const apiUrl = isServerSide 
        ? `http://localhost:${process.env.PORT || 5173}/api/scrape-website`
        : '/api/scrape-website';
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: campWebsite })
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.text && data.text.length > 100) {
          console.log('✅ [scrapePageTextComprehensive] Local scraper succeeded');
          if (data.emails && data.emails.length > 0) {
            console.log(`📧 [scrapePageTextComprehensive] Local scraper found ${data.emails.length} emails:`, data.emails);
          }
          return { 
            text: data.text, 
            html: data.html, 
            method: 'playwright-local',
            subPagesContent: [], // Local scraper only gets main page for now
            emails: data.emails || [] // Include emails found by local scraper
          };
        }
      }
      console.warn('⚠️ [scrapePageTextComprehensive] Local scraper failed, falling back to Apify');
    } catch (error) {
      console.warn('⚠️ [scrapePageTextComprehensive] Local scraper error, falling back to Apify:', error);
    }
  }
  
  // Fallback to Apify if not localhost or local scraper failed
  if (!APIFY_API_TOKEN) {
    console.warn('❌ Apify API token not configured');
    return { text: '', method: 'none' };
  }

  // Validate and normalize the website URL
  let normalizedUrl = campWebsite;
  try {
    // Ensure URL has proper protocol
    if (!normalizedUrl.startsWith('http://') && !normalizedUrl.startsWith('https://')) {
      normalizedUrl = 'http://' + normalizedUrl;
    }
    
    // Remove trailing slash for consistency
    normalizedUrl = normalizedUrl.replace(/\/$/, '');
    
    // Validate URL format
    new URL(normalizedUrl);
    console.log(`[scrapePageTextComprehensive] Normalized URL: ${normalizedUrl}`);
  } catch (error) {
    console.error(`[scrapePageTextComprehensive] Invalid URL format: ${campWebsite}`, error);
    return { 
      text: `Invalid URL format: ${campWebsite}. Please check the website URL.`, 
      method: 'none' 
    };
  }

  function isBoilerplate(text: string): boolean {
    if (!text || text.length < 100) return true;
    
    // Check if this looks like Google Sites content (including custom domains)
    // Google Sites can be on custom domains but still have Google Sites indicators
    const isGoogleSites = normalizedUrl.includes('sites.google.com') || 
                         text.includes('Google Sites') ||
                         text.includes('sites.google.com') ||
                         normalizedUrl.includes('googleusercontent.com');
    
    // For Google Sites, be more lenient with JS artifacts since they use a lot of JS
    const jsArtifacts = /window\.|ReactDOM|__APOLLO_STATE__|<script|<style|function\s*\(|var\s+|document\.|console\./g;
    const matches = text.match(jsArtifacts);
    const artifactRatio = matches ? matches.length / text.length : 0;
    
    // Google Sites can have up to 15% JS artifacts and still be valid content
    // Other sites should have less than 10%
    const threshold = isGoogleSites ? 0.15 : 0.1;
    
    return artifactRatio > threshold;
  }

  let mainPageText = '';
  let mainPageHtml = '';
  let method: 'playwright-local' | 'cheerio' | 'puppeteer' | 'none' = 'none';
  const subPagesContent: string[] = [];

  // First, get the main page content using Apify methods (local scraper was already tried above)
  // Only try Apify if local scraper didn't work
  if (method === 'none') {
    try {
      const cheerioResult = await scrapePageTextWithCheerioFast(normalizedUrl);
      if (!isBoilerplate(cheerioResult.text)) {
        mainPageText = cheerioResult.text;
        mainPageHtml = cheerioResult.html || '';
        method = 'cheerio';
        console.log('[scrapePageTextComprehensive] Main page Cheerio returned usable text.');
      }
    } catch (error) {
      console.warn('[scrapePageTextComprehensive] Main page Cheerio failed:', error);
    }

    // If Cheerio failed, try Puppeteer for main page
    if (!mainPageText) {
      try {
        const puppeteerText = await scrapePageTextWithPuppeteerFast(normalizedUrl);
        if (!isBoilerplate(puppeteerText)) {
          mainPageText = puppeteerText;
          method = 'puppeteer';
          console.log('[scrapePageTextComprehensive] Main page Puppeteer returned usable text.');
        }
      } catch (error) {
        console.warn('[scrapePageTextComprehensive] Main page Puppeteer failed:', error);
        // If Apify fails (e.g., quota exceeded), try local scraper as last resort
        if (!isLocalhost) {
          try {
            console.log('🔄 [scrapePageTextComprehensive] Apify failed, trying local scraper as last resort');
            
            // Build the API URL - use absolute URL for server-side, relative for client-side
            const apiUrl = isServerSide 
              ? `http://localhost:${process.env.PORT || 5173}/api/scrape-website`
              : '/api/scrape-website';
            
            const response = await fetch(apiUrl, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ url: normalizedUrl })
            });
            
            if (response.ok) {
              const data = await response.json();
              if (data.text && data.text.length > 100) {
                console.log('✅ [scrapePageTextComprehensive] Local scraper succeeded as fallback');
                mainPageText = data.text;
                mainPageHtml = data.html || '';
                method = 'playwright-local';
              }
            }
          } catch (localError) {
            console.warn('[scrapePageTextComprehensive] Local scraper fallback also failed:', localError);
          }
        }
      }
    }
  }

  // If we have main page content, try to discover and scrape sub-pages
  if (mainPageText && mainPageHtml) {
    try {
      // Use Cheerio to parse the HTML and discover sub-pages
      const cheerio = await import('cheerio');
      const $ = cheerio.load(mainPageHtml);
      const subPages = discoverCampSubpages($, normalizedUrl);
      
      console.log(`[scrapePageTextComprehensive] Found ${subPages.length} sub-pages to scrape:`, subPages);
      
      // Prioritize contact pages - extract them first, then take first 5 total
      const contactPages = subPages.filter(url => {
        const lower = url.toLowerCase();
        return lower.includes('/contact') || 
               lower.includes('contact-us') || 
               lower.includes('contactus') ||
               lower.includes('contact/') ||
               lower.includes('get-in-touch') ||
               lower.includes('reach-us');
      });
      
      const nonContactPages = subPages.filter(url => {
        const lower = url.toLowerCase();
        return !(lower.includes('/contact') || 
                 lower.includes('contact-us') || 
                 lower.includes('contactus') ||
                 lower.includes('contact/') ||
                 lower.includes('get-in-touch') ||
                 lower.includes('reach-us'));
      });
      
      // Combine: contact pages first, then other pages (limit to 5 total, but ensure at least 1 contact page if available)
      const subPagesToScrape = [
        ...contactPages.slice(0, 2), // Up to 2 contact pages
        ...nonContactPages.slice(0, Math.max(0, 5 - contactPages.length)) // Fill remaining slots
      ].slice(0, 5);
      
      console.log(`[scrapePageTextComprehensive] Prioritized sub-pages (${contactPages.length} contact, ${nonContactPages.length} other):`, subPagesToScrape);
      
      for (const subPageUrl of subPagesToScrape) {
        try {
          console.log(`[scrapePageTextComprehensive] Scraping sub-page: ${subPageUrl}`);
          
          // Try Cheerio first for sub-pages
          try {
            const subPageResult = await scrapePageTextWithCheerioFast(subPageUrl);
            if (!isBoilerplate(subPageResult.text)) {
              subPagesContent.push(subPageResult.text);
              console.log(`[scrapePageTextComprehensive] Sub-page Cheerio success: ${subPageUrl}`);
              continue;
            }
          } catch (error) {
            console.warn(`[scrapePageTextComprehensive] Sub-page Cheerio failed for ${subPageUrl}:`, error);
          }
          
          // Try Puppeteer fallback for sub-pages
          try {
            const subPageText = await scrapePageTextWithPuppeteerFast(subPageUrl);
            if (!isBoilerplate(subPageText)) {
              subPagesContent.push(subPageText);
              console.log(`[scrapePageTextComprehensive] Sub-page Puppeteer success: ${subPageUrl}`);
            }
          } catch (error) {
            console.warn(`[scrapePageTextComprehensive] Sub-page Puppeteer failed for ${subPageUrl}:`, error);
          }
          
        } catch (error) {
          console.warn(`[scrapePageTextComprehensive] Failed to scrape sub-page ${subPageUrl}:`, error);
        }
      }
      
    } catch (error) {
      console.warn('[scrapePageTextComprehensive] Failed to discover or scrape sub-pages:', error);
    }
  }

  // Combine main page and sub-pages content
  const allContent = [mainPageText, ...subPagesContent].filter(text => text && text.length > 0).join('\n\n');
  
  if (allContent) {
    console.log(`[scrapePageTextComprehensive] Successfully extracted ${allContent.length} characters of content`);
    return { 
      text: allContent, 
      html: mainPageHtml, 
      method, 
      subPagesContent 
    };
  } else {
    console.error('[scrapePageTextComprehensive] Failed to extract any usable text from:', campWebsite);
    
    // Try to provide some basic fallback content based on the URL
    const fallbackText = `Unable to extract content from ${campWebsite}. This may be due to:
- Website being temporarily unavailable
- Website blocking automated access
- Website having technical issues
- Website requiring JavaScript that our scraper cannot execute

Please try manual enrichment or check the website manually.`;
    
    return { text: fallbackText, method: 'none' };
  }
}

/**
 * Fast Cheerio text scraping with reduced timeouts
 */
async function scrapePageTextWithCheerioFast(campWebsite: string): Promise<{ text: string; html?: string }> {
  console.log('[scrapePageTextWithCheerioFast] Starting fast text extraction for:', campWebsite);
  
  // Increased timeout to 30s for better reliability on slower websites
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 30000);
  
  try {
    const runResponse = await fetch(`${APIFY_BASE_URL}/acts/apify~cheerio-scraper/runs`, {
      signal: controller.signal,
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${APIFY_API_TOKEN}`
      },
      body: JSON.stringify({
        startUrls: [{ url: campWebsite }],
        pageFunction: `async function pageFunction(context) {
          const { $, request, log } = context;
          
          try {
            log.info('Fast text extraction for: ' + request.url);
            
            // Remove script and style tags
            $('script, style, noscript').remove();
            
            // Extract main content with simplified logic
            let allText = '';
            
            // Get title
            const title = $('title').text().trim();
            if (title) {
              allText += 'Page Title: ' + title + '\\n\\n';
            }
            
            // Get meta description
            const metaDesc = $('meta[name="description"]').attr('content');
            if (metaDesc) {
              allText += 'Meta Description: ' + metaDesc + '\\n\\n';
            }
            
            // Get main content - simplified selectors
            const contentSelectors = [
              'main', '.main', '#main', '.content', '#content', 
              'article', '.article', '.post-content', '.entry-content',
              'body'
            ];
            
            for (const selector of contentSelectors) {
              const elements = $(selector);
              if (elements.length > 0) {
                const text = elements.text().trim();
                if (text && text.length > 100) {
                  allText += text + '\\n\\n';
                  break;
                }
              }
            }
            
            // Specifically capture footer content for contact information
            const footerSelectors = [
              'footer', '.footer', '#footer', 
              '.contact', '#contact', '.contact-info', '#contact-info',
              '.address', '#address', '.location', '#location',
              '.site-info', '.site-footer', '.page-footer'
            ];
            
            let footerText = '';
            for (const selector of footerSelectors) {
              const elements = $(selector);
              if (elements.length > 0) {
                const text = elements.text().trim();
                if (text && text.length > 10) {
                  footerText += text + '\\n';
                }
              }
            }
            
            if (footerText) {
              allText += '\\n--- FOOTER/CONTACT INFO ---\\n' + footerText + '\\n';
            }
            
            // Fallback to body if no main content found
            if (allText.length < 200) {
              allText += $('body').text().trim();
            }
            
            // Clean up the text
            allText = allText
              .replace(/\\s+/g, ' ')
              .replace(/\\n\\s*\\n/g, '\\n\\n')
              .trim();
            
            return { url: request.url, allText, htmlContent: $.html() };
          } catch (error) {
            log.error('Error in fast text extraction:', error.message);
            return { url: request.url, allText: '', error: error.message };
          }
        }`,
        proxy: {
          useApifyProxy: true,
          apifyProxyGroups: ['RESIDENTIAL']
        }
      })
    });
    
    if (!runResponse.ok) {
      const errorText = await runResponse.text();
      console.error(`[scrapePageTextWithCheerioFast] Apify API error for ${campWebsite}:`, runResponse.status, errorText);
      
      if (runResponse.status === 429) {
        throw new Error('Apify rate limit exceeded. Please try again later.');
      } else if (runResponse.status === 402) {
        throw new Error('Apify quota exceeded. Please check your account limits.');
      } else if (runResponse.status >= 500) {
        throw new Error('Apify service temporarily unavailable. Please try again later.');
      } else {
        throw new Error(`Fast Cheerio text scrape failed with status ${runResponse.status}: ${errorText}`);
      }
    }
    
    const runData = await runResponse.json();
    if (!runData.data?.id) {
      throw new Error('No run ID from fast Cheerio text');
    }
    
    // Wait for run to complete with timeout
    let attempts = 0;
    const maxAttempts = 60; // Increased back to 60 for better reliability on slower websites
    while (attempts < maxAttempts) {
      await new Promise(res => setTimeout(res, 1000));
      console.log(`[scrapePageTextWithCheerioFast] Checking status attempt ${attempts + 1}/${maxAttempts} for ${campWebsite}`);
      
      const statusResponse = await fetch(`${APIFY_BASE_URL}/acts/apify~cheerio-scraper/runs/${runData.data.id}`, {
        headers: { 'Authorization': `Bearer ${APIFY_API_TOKEN}` }
      });
      
      if (!statusResponse.ok) {
        console.error('Fast Cheerio text status check failed:', statusResponse.status);
        break;
      }
      
      const statusData = await statusResponse.json();
      const runStatus = statusData.data?.status || statusData.status;
      
      console.log(`[scrapePageTextWithCheerioFast] Run status: ${runStatus} for ${campWebsite}`);
      
      if (runStatus === 'SUCCEEDED') break;
      if (runStatus === 'FAILED' || runStatus === 'ABORTED') {
        throw new Error('Fast Cheerio text run failed');
      }
      attempts++;
    }
    
    if (attempts >= maxAttempts) {
      throw new Error('Fast Cheerio text run timed out');
    }
    
    // Get the results
    const datasetResponse = await fetch(`${APIFY_BASE_URL}/datasets/${runData.data.defaultDatasetId}/items`, {
      headers: { 'Authorization': `Bearer ${APIFY_API_TOKEN}` }
    });
    
    if (!datasetResponse.ok) {
      throw new Error('Failed to fetch fast Cheerio text dataset');
    }
    
    const datasetData = await datasetResponse.json();
    
    if (!Array.isArray(datasetData) || !datasetData[0]) {
      return { text: '', html: undefined };
    }
    
    const result = datasetData[0];
    const extractedText = result.allText || '';
    
    return { 
      text: extractedText, 
      html: result.htmlContent 
    };
    
  } catch (error) {
    console.error('[scrapePageTextWithCheerioFast] Error:', error);
    
    // Check for specific error types
    if (error instanceof Error) {
      if (error.message.includes('timeout') || error.message.includes('timed out')) {
        console.error(`[scrapePageTextWithCheerioFast] Timeout occurred for ${campWebsite} after 30 seconds`);
      } else if (error.message.includes('fetch')) {
        console.error(`[scrapePageTextWithCheerioFast] Network error for ${campWebsite}:`, error.message);
      } else if (error.message.includes('Failed to fetch')) {
        console.error(`[scrapePageTextWithCheerioFast] Dataset fetch failed for ${campWebsite}:`, error.message);
      }
    }
    
    clearTimeout(timeoutId);
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * Fast Puppeteer text scraping with reduced timeouts
 */
async function scrapePageTextWithPuppeteerFast(campWebsite: string): Promise<string> {
  console.log('[scrapePageTextWithPuppeteerFast] Starting fast text extraction for:', campWebsite);
  
  // Increased timeout to 30s for better reliability on slower websites
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 30000);
  
  try {
    const runResponse = await fetch(`${APIFY_BASE_URL}/acts/apify~web-scraper/runs`, {
      signal: controller.signal,
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${APIFY_API_TOKEN}`
      },
      body: JSON.stringify({
        startUrls: [{ url: campWebsite }],
        pageFunction: `async function pageFunction(context) {
          const { page, request, log } = context;
          try {
            // Set a modern user agent
            await page.setUserAgent('Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/120.0.0.0 Safari/537.36');
            
            // Reduced wait time from 15s to 5s
            await new Promise(resolve => setTimeout(resolve, 5000));
            
            // Remove script and style tags
            await page.evaluate(() => {
              document.querySelectorAll('script, style, noscript').forEach(el => el.remove());
            });
            
            // Simplified content extraction
            const mainText = await page.evaluate(() => {
              const selectors = [
                'main', '.main', '#main', '.content', '#content', 
                'article', '.article', '.post-content', '.entry-content',
                'body'
              ];
              
              let mainContent = '';
              for (const selector of selectors) {
                const elements = document.querySelectorAll(selector);
                if (elements.length > 0) {
                  let text = '';
                  for (const element of elements) {
                    text += (element.textContent || element.innerText || '') + ' ';
                  }
                  if (text.trim().length > 100) {
                    mainContent = text.trim();
                    break;
                  }
                }
              }
              
              // Specifically capture footer content for contact information
              const footerSelectors = [
                'footer', '.footer', '#footer', 
                '.contact', '#contact', '.contact-info', '#contact-info',
                '.address', '#address', '.location', '#location',
                '.site-info', '.site-footer', '.page-footer'
              ];
              
              let footerContent = '';
              for (const selector of footerSelectors) {
                const elements = document.querySelectorAll(selector);
                if (elements.length > 0) {
                  let text = '';
                  for (const element of elements) {
                    text += (element.textContent || element.innerText || '') + ' ';
                  }
                  if (text.trim().length > 10) {
                    footerContent += text.trim() + ' ';
                  }
                }
              }
              
              // Combine main content and footer content
              let combinedText = mainContent;
              if (footerContent) {
                combinedText += '\\n\\n--- FOOTER/CONTACT INFO ---\\n' + footerContent.trim();
              }
              
              // Fallback to body if no main content found
              if (!combinedText || combinedText.length < 200) {
                combinedText = document.body.textContent || document.body.innerText || '';
              }
              
              return combinedText;
            });
            
            // Clean up the text
            const cleanedText = mainText
              .replace(/\\s+/g, ' ')
              .replace(/\\n\\s*\\n/g, '\\n\\n')
              .trim();
            
            return {
              url: request.url,
              mainText: cleanedText,
              title: await page.title()
            };
          } catch (error) {
            log.error('Error in fast Puppeteer text extraction:', error);
            return {
              url: request.url,
              mainText: '',
              title: '',
              error: error.message
            };
          }
        }`,
        proxy: {
          useApifyProxy: true,
          apifyProxyGroups: ['RESIDENTIAL']
        }
      })
    });
    
    if (!runResponse.ok) {
      const errorText = await runResponse.text();
      console.error(`[scrapePageTextWithPuppeteerFast] Apify API error for ${campWebsite}:`, runResponse.status, errorText);
      
      if (runResponse.status === 429) {
        throw new Error('Apify rate limit exceeded. Please try again later.');
      } else if (runResponse.status === 402) {
        throw new Error('Apify quota exceeded. Please check your account limits.');
      } else if (runResponse.status >= 500) {
        throw new Error('Apify service temporarily unavailable. Please try again later.');
      } else {
        throw new Error(`Fast Puppeteer text scrape failed with status ${runResponse.status}: ${errorText}`);
      }
    }
    
    const runData = await runResponse.json();
    if (!runData.data?.id) {
      throw new Error('No run ID from fast Puppeteer text');
    }
    
    // Wait for run to complete with timeout
    let attempts = 0;
    const maxAttempts = 8; // Reduced from 10
    while (attempts < maxAttempts) {
      await new Promise(res => setTimeout(res, 1000));
      console.log(`[scrapePageTextWithPuppeteerFast] Checking status attempt ${attempts + 1}/${maxAttempts} for ${campWebsite}`);
      
      const statusResponse = await fetch(`${APIFY_BASE_URL}/acts/apify~web-scraper/runs/${runData.data.id}`, {
        headers: { 'Authorization': `Bearer ${APIFY_API_TOKEN}` }
      });
      const statusData = await statusResponse.json();
      const runStatus = statusData.data?.status || statusData.status;
      
      console.log(`[scrapePageTextWithPuppeteerFast] Run status: ${runStatus} for ${campWebsite}`);
      
      if (runStatus === 'SUCCEEDED') break;
      if (runStatus === 'FAILED' || runStatus === 'ABORTED') {
        throw new Error('Fast Puppeteer text run failed');
      }
      attempts++;
    }
    
    // Get the results
    const datasetResponse = await fetch(`${APIFY_BASE_URL}/datasets/${runData.data.defaultDatasetId}/items`, {
      headers: { 'Authorization': `Bearer ${APIFY_API_TOKEN}` }
    });
    const datasetData = await datasetResponse.json();
    
    clearTimeout(timeoutId);
    
    if (!Array.isArray(datasetData) || !datasetData[0]) return '';
    return datasetData[0].mainText || '';
    
  } catch (error) {
    console.error('[scrapePageTextWithPuppeteerFast] Error:', error);
    clearTimeout(timeoutId);
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * Scrape Facebook page for contact information (address, phone, etc.)
 */
export async function scrapeFacebookPage(facebookUrl: string): Promise<{
  address?: string;
  phone?: string;
  email?: string;
  website?: string;
  name?: string;
  title?: string;
}> {
  console.log('[scrapeFacebookPage] Starting Facebook page scraping for:', facebookUrl);
  
  if (!facebookUrl || !facebookUrl.includes('facebook.com')) {
    console.warn('[scrapeFacebookPage] Invalid Facebook URL:', facebookUrl);
    return {};
  }
  
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 60000); // 60 second timeout
  
  try {
    
            // Use the Apify Facebook pages scraper actor
            const facebookActorId = 'apify~facebook-pages-scraper';
    console.log(`[scrapeFacebookPage] Using Apify Facebook pages scraper actor ${facebookActorId} for: ${facebookUrl}`);
    
    // Use the Apify Facebook pages scraper actor directly
    console.log(`[scrapeFacebookPage] Using Apify Facebook pages scraper actor ${facebookActorId} for: ${facebookUrl}`);
    
    const runResponse = await fetch(`${APIFY_BASE_URL}/acts/${facebookActorId}/run-sync-get-dataset-items`, {
      signal: controller.signal,
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${APIFY_API_TOKEN}`
      },
      body: JSON.stringify({
        startUrls: [{ url: facebookUrl }],
        maxItems: 1
      })
    });
    
    if (!runResponse.ok) {
      const errorText = await runResponse.text();
      console.error(`[scrapeFacebookPage] Apify API error for ${facebookUrl}:`, runResponse.status, errorText);
      throw new Error(`Facebook page scraping failed with status ${runResponse.status}: ${errorText}`);
    }
    
    const datasetData = await runResponse.json();
    
    if (!datasetData || datasetData.length === 0) {
      console.warn('[scrapeFacebookPage] No data returned from Facebook page scraping');
      return {};
    }
    
    const pageData = datasetData[0];
    console.log('[scrapeFacebookPage] Facebook Pages Scraper data:', pageData);
    
    clearTimeout(timeoutId);
    return {
      address: pageData.address || pageData.streetAddress || undefined,
      phone: pageData.phone || pageData.phoneNumber || undefined,
      email: pageData.email || pageData.emailAddress || undefined,
      website: pageData.website || pageData.websiteUrl || undefined,
      name: pageData.name || pageData.title || pageData.pageName || undefined,
      title: pageData.title || pageData.name || pageData.pageName || undefined
    };
    
  } catch (error) {
    console.error('[scrapeFacebookPage] Error:', error);
    clearTimeout(timeoutId);
    return {};
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * Scrape directory listings (summercamps.com, etc.) for camp information
 * Used as fallback when main website is blocked by Cloudflare
 */
export async function scrapeDirectoryListings(
  campName: string, 
  city?: string, 
  state?: string
): Promise<string> {
  console.log('[scrapeDirectoryListings] Searching directory sites for:', { campName, city, state });
  
  const APIFY_API_TOKEN = import.meta.env.VITE_APIFY_API_TOKEN;
  if (!APIFY_API_TOKEN) {
    console.warn('[scrapeDirectoryListings] Apify API token not configured');
    return '';
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 45000); // 45 second timeout

  try {
    // Build search query for summercamps.com
    const searchQuery = city && state 
      ? `${campName} ${city} ${state}`
      : campName;
    
    // Try searching summercamps.com
    const searchUrl = `https://www.summercamps.com/?s=${encodeURIComponent(searchQuery)}`;
    
    console.log('[scrapeDirectoryListings] Searching summercamps.com directory...');
    
    const response = await fetch(`${APIFY_BASE_URL}/acts/apify~web-scraper/run-sync-get-dataset-items`, {
      signal: controller.signal,
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${APIFY_API_TOKEN}`
      },
      body: JSON.stringify({
        startUrls: [{ url: searchUrl }],
        pageFunction: `
          async function pageFunction(context) {
            const { page, request, log } = context;
            try {
              await page.waitForTimeout(3000);
              
              // Extract search results
              const results = [];
              
              // Look for camp listings in search results
              const listings = await page.$$eval('.camp-listing, .search-result, article, .camp-card', (elements) => {
                return elements.map(el => {
                  const title = el.querySelector('h2, h3, .title, a')?.textContent?.trim() || '';
                  const description = el.querySelector('.description, .excerpt, p')?.textContent?.trim() || '';
                  const link = el.querySelector('a')?.href || '';
                  const location = el.querySelector('.location, .city-state')?.textContent?.trim() || '';
                  
                  return {
                    title,
                    description,
                    link,
                    location,
                    text: [title, description, location].filter(Boolean).join(' ')
                  };
                });
              }).catch(() => []);
              
              // Also try to extract general page text
              const bodyText = await page.evaluate(() => {
                return document.body?.innerText || '';
              });
              
              return {
                url: request.url,
                listings: listings || [],
                bodyText: bodyText || '',
                foundResults: (listings || []).length > 0
              };
            } catch (error) {
              log.error('Error in directory scraping:', error.message);
              return {
                url: request.url,
                listings: [],
                bodyText: '',
                foundResults: false,
                error: error.message
              };
            }
          }
        `,
        proxy: {
          useApifyProxy: true,
          apifyProxyGroups: ['RESIDENTIAL']
        },
        maxRequestRetries: 2,
        maxConcurrency: 1
      })
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`[scrapeDirectoryListings] Apify API error: ${response.status}`, errorText);
      clearTimeout(timeoutId);
      return '';
    }

    const datasetData = await response.json();
    
    if (!datasetData || datasetData.length === 0) {
      console.warn('[scrapeDirectoryListings] No data returned from directory search');
      clearTimeout(timeoutId);
      return '';
    }

    const pageData = datasetData[0];
    
    // Combine listings and body text
    let combinedText = '';
    
    if (pageData.listings && pageData.listings.length > 0) {
      // Filter for listings that match the camp name
      const campNameLower = campName.toLowerCase();
      const matchingListings = pageData.listings.filter((listing: any) => 
        listing.title?.toLowerCase().includes(campNameLower) ||
        listing.text?.toLowerCase().includes(campNameLower)
      );
      
      if (matchingListings.length > 0) {
        combinedText = matchingListings.map((listing: any) => 
          `${listing.title || ''}\n${listing.description || ''}\n${listing.location || ''}\n${listing.link || ''}`
        ).join('\n\n---\n\n');
        
        console.log(`[scrapeDirectoryListings] Found ${matchingListings.length} matching listings`);
      }
    }
    
    // Add general page text if we found results
    if (pageData.foundResults && pageData.bodyText) {
      combinedText = combinedText ? `${combinedText}\n\n${pageData.bodyText.substring(0, 5000)}` : pageData.bodyText.substring(0, 5000);
    }

    clearTimeout(timeoutId);
    return combinedText;
    
  } catch (error) {
    console.error('[scrapeDirectoryListings] Error:', error);
    clearTimeout(timeoutId);
    return '';
  } finally {
    clearTimeout(timeoutId);
  }
}
